<template>
  <main class="feature-detail-page sst-wide grow" :class="{ 'is-story': story }">
    <section class="feature-detail-hero">
      <div class="feature-shell">
        <nav class="feature-breadcrumb" aria-label="Breadcrumb">
          <NuxtLink :to="localizedPath('/features/', currentLocale)">Features</NuxtLink>
          <span aria-hidden="true">/</span>
          <span>{{ feature.eyebrow }}</span>
        </nav>

        <div class="feature-hero-grid">
          <header class="feature-hero-copy">
            <h1>{{ story?.heroTitle || feature.title }}</h1>
            <p class="feature-intro">{{ feature.intro }}</p>

            <div ref="heroActions" class="feature-hero-actions">
              <a
                :href="handleReferral(signupUrl)"
                class="feature-primary"
                data-conversion-event="start_free_click"
                :data-conversion-label="`${feature.title} primary CTA`"
                :data-conversion-location="route.path"
              >
                Start free trial
              </a>
              <!-- Terms directly under the trial button (B1.1): on phones the buttons stack, so the line sits between them;
                   from 641px it takes its own row under both. -->
              <TrialDisclosure :plan="ctaPlan" />
              <NuxtLink
                :to="localizedPath('/contact-sales', currentLocale)"
                class="feature-secondary"
                data-conversion-event="demo_request_click"
                :data-conversion-label="`${feature.title} demo CTA`"
                :data-conversion-location="route.path"
              >
                Request a demo
              </NuxtLink>
            </div>
            <!-- After the terms, never between the trial button and them (B1.1). -->
            <p v-if="story?.heroPlanNote" class="feature-hero-plan-note">{{ story.heroPlanNote }}</p>

            <dl v-if="!story" class="feature-hero-facts" aria-label="Feature summary">
              <div v-for="(point, index) in feature.points" :key="point">
                <dt>{{ String(index + 1).padStart(2, "0") }}</dt>
                <dd>{{ point }}</dd>
              </div>
            </dl>
          </header>

          <StoryChatStage v-if="story?.hero === 'ai-chat'" class="feature-hero-frame feature-hero-story" autoplay :rest="story.heroRest || 'done'" />
          <!-- Money pages: a product replica (StoryReplica) that plays its states once. -->
          <StoryReplicaStage
            v-else-if="story?.hero?.replica"
            class="feature-hero-frame feature-hero-story"
            :class="{ 'feature-hero-story--app': story.hero.app }"
            :replica="story.hero.replica"
            :app="story.hero.app"
            :states="story.hero.states"
            :holds="story.hero.holds"
            :label="story.hero.label"
            autoplay
          />
          <!-- App-screen heroes (components/story/screens) carry heroStates; the calls and inbox
               heroes (StoryHero) play their own scene. -->
          <StoryStage
            v-else-if="story?.heroStates"
            class="feature-hero-frame feature-hero-story"
            :screen="story.hero"
            :states="story.heroStates"
            :hold="story.heroHold"
            :label="story.heroLabel"
            autoplay
          />
          <!-- The online booking hero: the public booking page replica playing one sample booking. -->
          <StoryBookingStage
            v-else-if="story?.hero === 'booking'"
            class="feature-hero-frame feature-hero-story"
            :sample="story.heroSample"
            :label="story.heroLabel"
            autoplay
            tall
          />
          <StoryHero v-else-if="story?.hero" :hero="story.hero" :label="story.heroLabel" class="feature-hero-frame feature-hero-story" />
          <FeatureProductFrame
            v-else
            class="feature-hero-frame"
            :title="feature.sidebarTitle"
            :caption="feature.sidebarCopy"
            :label="feature.heroLabel"
            :image="feature.heroImage"
            :image-alt="feature.heroImageAlt"
            :mode="feature.heroMode"
            priority
          />
        </div>
        <!-- Story pages: the points sit under the product as one strip. -->
        <dl v-if="story" class="feature-hero-facts" aria-label="Feature summary">
          <div v-for="(point, index) in feature.points" :key="point">
            <dt>{{ String(index + 1).padStart(2, "0") }}</dt>
            <dd>{{ point }}</dd>
          </div>
        </dl>
      </div>
    </section>

    <section id="what-is" class="feature-definition-section">
      <div class="feature-shell feature-definition-grid">
        <div>
          <h2>{{ feature.definitionTitle }}</h2>
        </div>
        <div>
          <p class="feature-definition-copy">{{ feature.definition }}</p>
          <div class="feature-reference">
            <span>Product reference</span>
            <p>
              Reviewed {{ feature.reviewedOn || "July 17, 2026" }} by the Workspace369
              Product Team. Availability can vary by plan, region, and workspace
              configuration.
            </p>
            <a
              v-if="feature.docsUrl"
              :href="feature.docsUrl"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ feature.docsLabel || "Read the product documentation" }}
              <span aria-hidden="true">-&gt;</span>
            </a>
          </div>
        </div>
      </div>
    </section>

    <FeatureTutorial v-if="featureTutorial" :tutorial="featureTutorial" />

    <section v-if="feature.intakeChannels" class="feature-capabilities-section">
      <div class="feature-shell">
        <header class="feature-section-heading">
          <h2>{{ feature.intakeTitle }}</h2>
          <p class="feature-definition-copy">{{ feature.intakeAnswer }}</p>
        </header>
        <div class="intake-table" tabindex="0" role="region" :aria-label="feature.intakeTitle">
          <table>
            <caption>Client request intake: capture, plan and review</caption>
            <thead><tr><th scope="col">Channel</th><th scope="col">Capture step</th><th scope="col">Plan and setup</th><th scope="col">Human review</th></tr></thead>
            <tbody><tr v-for="row in feature.intakeChannels" :key="row.channel"><th scope="row">{{ row.channel }}</th><td>{{ row.capture }}</td><td>{{ row.plan }}</td><td>{{ row.review }}</td></tr></tbody>
          </table>
        </div>
        <p>{{ feature.intakeExample }}</p>
        <NuxtLink :to="localizedPath('/pricing', currentLocale)">Check current plan requirements</NuxtLink>
      </div>
    </section>

    <!-- Story pages replace these generic sections, except the ones their story lists in `keep`. -->
    <template v-if="!story || story.keep?.length">
      <section v-if="feature.capabilities?.length && keeps('capabilities')" class="feature-capabilities-section">
        <div class="feature-shell">
          <header class="feature-section-heading feature-section-heading--split">
            <div>
              <h2>{{ feature.capabilitiesTitle }}</h2>
            </div>
            <p>{{ feature.capabilitiesIntro }}</p>
          </header>

          <div class="feature-capabilities-grid" :data-count="feature.capabilities.length">
            <article v-for="item in feature.capabilities" :key="item.title">
              <span>{{ item.label }}</span>
              <h3>{{ item.title }}</h3>
              <p>{{ item.copy }}</p>
            </article>
          </div>
        </div>
      </section>

      <section v-if="keeps('flow')" class="feature-flow-section">
        <div class="feature-shell">
          <header class="feature-section-heading feature-section-heading--split">
            <div>
              <p>Built into the operating flow</p>
              <h2>{{ feature.workflowTitle }}</h2>
            </div>
            <p>{{ feature.connects }}</p>
          </header>

          <ol class="feature-flow-list" :data-count="feature.workflow?.length">
            <li v-for="(step, index) in feature.workflow" :key="step.title">
              <span>{{ String(index + 1).padStart(2, "0") }}</span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.copy }}</p>
            </li>
          </ol>
        </div>
      </section>

      <aside v-if="!story" class="feature-mid-cta" aria-label="Start a free trial">
        <div class="feature-shell feature-mid-cta__inner">
          <p>{{ feature.sidebarTitle }} — {{ feature.sidebarCopy }}</p>
          <a
            :href="handleReferral(signupUrl)"
            class="feature-primary feature-primary--light"
            data-conversion-event="start_free_click"
            :data-conversion-label="`${feature.title} mid-page CTA`"
            :data-conversion-location="route.path"
          >
            Start free trial
          </a>
          <div class="feature-trial-note"><TrialDisclosure :plan="ctaPlan" on-dark /></div>
        </div>
      </aside>

      <section v-if="keeps('showcase')" class="feature-showcase-section">
        <div class="feature-shell">
          <header class="feature-section-heading">
            <h2>{{ feature.showcaseTitle }}</h2>
            <span>{{ feature.showcaseIntro }}</span>
          </header>

          <!-- With a motion scene, the steps run down the left and the scene stays in view beside
               them; only real screenshots keep their frame. -->
          <div class="feature-showcase-list" :class="{ 'feature-showcase-list--motion': motion }">
            <MotionStage v-if="motion" class="feature-motion" :config="motion" />
            <div class="feature-showcase-steps">
            <article
              v-for="(item, index) in feature.showcase"
              :key="item.title"
              class="feature-showcase-row"
            >
              <div class="feature-showcase-copy">
                <h3>{{ item.title }}</h3>
                <p>{{ item.copy }}</p>
                <ul v-if="item.details?.length">
                  <li v-for="detail in item.details" :key="detail">{{ detail }}</li>
                </ul>
              </div>

              <FeatureProductFrame
                v-if="!motion || item.image"
                :title="item.title"
                :caption="item.caption || item.copy"
                :label="item.label || feature.sidebarTitle"
                :image="item.image || ''"
                :image-alt="item.imageAlt || item.title"
                :mode="item.mode || 'records'"
              />
            </article>
            </div>
          </div>
        </div>
      </section>
    </template>

    <StoryFeatureStory v-if="story" :story="story">
      <template #mid>
        <aside class="feature-mid-cta feature-mid-cta--quiet" aria-label="Start a free trial">
          <div class="feature-shell feature-mid-cta__inner">
            <p><strong>{{ feature.sidebarTitle }}</strong> {{ feature.sidebarCopy }}</p>
            <a
              :href="handleReferral(signupUrl)"
              class="feature-primary"
              data-conversion-event="start_free_click"
              :data-conversion-label="`${feature.title} mid-page CTA`"
              :data-conversion-location="route.path"
            >
              Start free trial
            </a>
            <div class="feature-trial-note"><TrialDisclosure :plan="ctaPlan" /></div>
          </div>
        </aside>
      </template>
    </StoryFeatureStory>

    <section v-if="feature.examples?.length && keeps('examples')" class="feature-examples-section">
      <div class="feature-shell">
        <header class="feature-section-heading feature-section-heading--split">
          <div>
            <h2>{{ feature.examplesTitle }}</h2>
          </div>
          <p>{{ feature.examplesIntro }}</p>
        </header>

        <div class="feature-examples-grid" :data-count="feature.examples.length">
          <article v-for="example in feature.examples" :key="example.name" class="feature-example">
            <header>
              <h3>{{ example.name }}</h3>
              <div class="feature-example__meta">
                <span v-if="example.tag" class="feature-example__tag">{{ example.tag }}</span>
                <span class="feature-example__status">{{ example.status }}</span>
              </div>
            </header>
            <ol>
              <li
                v-for="(step, index) in example.steps"
                :key="index"
                :class="`feature-xstep--${step.tone}`"
              >
                <i aria-hidden="true">{{ index + 1 }}</i>
                <span>{{ step.text }}</span>
              </li>
            </ol>
          </article>
        </div>
      </div>
    </section>

    <!-- Story pages already show what it works with (flow chapter), so these two would repeat it. -->
    <section v-if="!story" class="feature-use-cases-section">
      <div class="feature-shell">
        <header class="feature-section-heading feature-section-heading--split">
          <div>
            <p>Common operating scenarios</p>
            <h2>{{ feature.useCasesTitle }}</h2>
          </div>
          <p>
            These examples describe how the feature fits into connected client work. They
            are product workflows, not promised business outcomes.
          </p>
        </header>

        <div class="feature-use-cases-grid" :data-count="feature.useCases?.length">
          <article v-for="(item, index) in feature.useCases" :key="item.title">
            <span>{{ String(index + 1).padStart(2, "0") }}</span>
            <h3>{{ item.title }}</h3>
            <p>{{ item.copy }}</p>
          </article>
        </div>
      </div>
    </section>

    <section v-if="!story" class="feature-connected-section">
      <div class="feature-shell feature-connected-grid">
        <div>
          <h2>One record can carry the work forward.</h2>
        </div>
        <div>
          <p>{{ feature.connects }}</p>
          <nav v-if="feature.relatedLinks?.length" class="feature-related-links" aria-label="Related guides">
            <NuxtLink
              v-for="link in feature.relatedLinks"
              :key="link.path"
              :to="localizedPath(link.path, currentLocale)"
            >
              {{ link.label }}
              <span aria-hidden="true">-&gt;</span>
            </NuxtLink>
          </nav>
        </div>
      </div>
    </section>

    <section v-if="feature.answer" class="feature-answer-section">
      <div class="feature-shell feature-answer-grid">
        <h2>{{ feature.answer.title }}</h2>
        <div>
          <p>{{ feature.answer.copy }}</p>
          <NuxtLink
            v-if="feature.answer.path"
            :to="localizedPath(feature.answer.path, currentLocale)"
          >
            {{ feature.answer.linkLabel }}
            <span aria-hidden="true">-&gt;</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section v-if="feature.faq?.length" id="faq" class="feature-faq-section">
      <div class="feature-shell feature-faq-grid">
        <header>
          <h2>{{ feature.faqTitle }}</h2>
        </header>
        <div>
          <details v-for="(item, index) in feature.faq" :key="item.question" :open="story && index === 0">
            <summary>{{ item.question }}</summary>
            <p>{{ item.answer }}</p>
          </details>
        </div>
      </div>
    </section>

    <FeatureReleaseUpdates :path="`/features/${featureKey}`" />

    <section v-if="feature.relatedFeatures?.length" class="feature-related-section">
      <div class="feature-shell">
        <header class="feature-section-heading feature-section-heading--split">
          <div>
            <h2>Related features</h2>
          </div>
          <p>{{ feature.relatedFeaturesIntro }}</p>
          <!-- Story pages drop the connected section that carries these links elsewhere, so they sit
               here, under the intro, minus any feature already listed on the right. -->
          <nav v-if="storyReadNext.length" class="feature-read-next" aria-labelledby="feature-read-next-title">
            <h3 id="feature-read-next-title">Keep reading</h3>
            <ul>
              <li v-for="link in storyReadNext" :key="link.path">
                <!-- No space before the arrow, so a wrapped label keeps it on its last word. -->
                <NuxtLink :to="localizedPath(link.path, currentLocale)">{{ link.label }}<span aria-hidden="true">→</span></NuxtLink>
              </li>
            </ul>
          </nav>
        </header>

        <div class="feature-related-grid">
          <NuxtLink
            v-for="item in (story ? feature.relatedFeatures.slice(0, 3) : feature.relatedFeatures)"
            :key="item.path"
            :to="localizedPath(item.path, currentLocale)"
            class="feature-related-card"
          >
            <span>{{ item.name }}</span>
            <p>{{ item.copy }}</p>
            <em>Explore feature <i aria-hidden="true">→</i></em>
          </NuxtLink>
        </div>
      </div>
    </section>

    <section class="feature-final-cta">
      <div class="feature-shell feature-final-cta__inner">
        <h2>{{ story?.ctaTitle || feature.ctaTitle }}</h2>
        <p class="feature-final-cta__copy">{{ feature.ctaCopy }}</p>
        <div class="feature-hero-actions feature-hero-actions--center">
          <a
            :href="handleReferral(signupUrl)"
            class="feature-primary"
            data-conversion-event="start_free_click"
            :data-conversion-label="`${feature.title} final CTA`"
            :data-conversion-location="route.path"
          >
            Start free trial
          </a>
          <NuxtLink
            :to="localizedPath('/contact-sales', currentLocale)"
            class="feature-final-cta__ghost"
            data-conversion-event="demo_request_click"
            :data-conversion-label="`${feature.title} final demo CTA`"
            :data-conversion-location="route.path"
          >
            Request a demo
          </NuxtLink>
        </div>
        <!-- The trial terms sit directly under the trial button (legal B1.1). The pricing link follows the terms:
             in the button row it wrapped under the trial button on narrow and 1024-1280 px screens. -->
        <div class="feature-final-cta__trial">
          <TrialDisclosure :plan="ctaPlan" on-dark />
          <NuxtLink
            :to="localizedPath('/pricing', currentLocale)"
            class="feature-textlink"
            data-conversion-event="pricing_click"
            :data-conversion-label="`${feature.title} final pricing link`"
            :data-conversion-location="route.path"
          >
            See pricing
            <span aria-hidden="true">→</span>
          </NuxtLink>
        </div>
      </div>
    </section>

    <div class="feature-sticky-cta" :class="{ 'is-shown': stickyShown }">
      <a
        :href="handleReferral(signupUrl)"
        class="feature-primary"
        data-conversion-event="start_free_click"
        :data-conversion-label="`${feature.title} sticky mobile CTA`"
        :data-conversion-location="route.path"
      >
        Start free trial
      </a>
      <NuxtLink
        :to="localizedPath('/contact-sales', currentLocale)"
        class="feature-secondary"
        data-conversion-event="demo_request_click"
        :data-conversion-label="`${feature.title} sticky demo CTA`"
        :data-conversion-location="route.path"
      >
        Request a demo
      </NuxtLink>
      <TrialDisclosure :plan="ctaPlan" compact centered class="feature-trial-note" />
    </div>
  </main>
</template>

<script setup>
import featureVideos from "#build/page-data/feature-videos.mjs";
import featureSeoLoaders from "#build/page-data/feature-seo-loaders.mjs";
import { localizedPath, localizedUrl } from "~/data/locales";
import { featureMotion } from "~/data/feature-motion.js";
import storyLoaders from "#build/page-data/stories-loaders.mjs";

const props = defineProps({
  featureKey: {
    type: String,
    required: true,
  },
});

// The page's CSS is inlined in the head, so its HTML is ~85% parsed before the hero begins, and on a
// slow connection Chrome paints the hero half-parsed: on phones the product replica (ordered between
// the H1 and the intro) arrives after the intro has painted and pushes it off-screen, and on desktop
// the replica grows as its rows stream in (CLS 0.06-0.40 under slow 4G, 2026-10-25). Holding the
// first paint until the section after the hero is parsed costs only the hero's own bytes.
useHead({ link: [{ rel: "expect", href: "#what-is", blocking: "render" }] });

// One generated module per page (modules/page-data.ts), so a page downloads only its own story and SEO layer.
const [storyData, seoEnhancement] = await Promise.all([
  storyLoaders[props.featureKey]?.(),
  (featureSeoLoaders[props.featureKey] || featureSeoLoaders["client-portal"])(),
]);

const motion = computed(() => featureMotion[`features/${props.featureKey}`]);
const { addReferralToUrl } = useReferral();
const handleReferral = (baseUrl) => addReferralToUrl(baseUrl);
// A story page whose feature starts on a plan (story.ctaPlan) signs up on that plan, and the trial
// line under each button names that plan's price.
const ctaPlan = computed(() => story.value?.ctaPlan || "");
const signupUrl = computed(() => (ctaPlan.value ? `https://app.workspace369.com/signup?plan=${ctaPlan.value}` : "https://app.workspace369.com/signup"));
const route = useRoute();
const currentLocale = computed(() => String(route.params.locale || "en"));

const featureTutorial = computed(() => featureVideos[`/features/${props.featureKey}`]);

// The sticky trial bar waits until the hero's own buttons leave the screen, so it never covers the hero product.
const heroActions = ref(null);
const stickyShown = ref(false);
let heroObserver;
onMounted(() => {
  if (!("IntersectionObserver" in window)) {
    stickyShown.value = true;
    return;
  }
  // The huge bottom margin counts "still below the fold" as in view, so the bar only shows once the buttons pass the top.
  heroObserver = new IntersectionObserver(
    ([entry]) => {
      stickyShown.value = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    },
    { rootMargin: "0px 0px 100000px 0px" },
  );
  // The hero can re-render after hydration, so follow the element rather than observing it once.
  watch(
    heroActions,
    (el, old) => {
      if (old) heroObserver.unobserve(old);
      if (el) heroObserver.observe(el);
      else stickyShown.value = true;
    },
    { immediate: true, flush: "post" },
  );
});
onBeforeUnmount(() => heroObserver?.disconnect());

const featureData = {
  "client-portal": {
    eyebrow: "Client Portal",
    title: "A private client portal for each client you work with",
    metaDescription:
      "Give each client a private, branded portal where they pay invoices, review proposals, sign contracts and follow projects, signed in with an email code.",
    intro:
      "Every client gets one link to their own page. They sign in with an email code and see what needs them: invoices to pay, proposals to review and contracts to sign.",
    points: [
      "One private page per client, opened with a 6-digit email code.",
      "Action items for invoices, proposals, contracts and change orders.",
      "Your logo, brand color and layout, with sections you choose.",
    ],
    connects:
      "The portal connects to invoices, proposals, contracts, change orders, projects, boards and retainers.",
    capabilitiesTitle: "What the client sees, and what stays private.",
    capabilitiesIntro:
      "The portal reads live from the client's records and shows only the sections you turn on.",
    capabilities: [
      {
        label: "Access",
        title: "Email-code sign-in",
        copy: "The client's emails and contact people sign in with a code. No passwords.",
      },
      {
        label: "Actions",
        title: "Action items",
        copy: "Invoices due, proposals awaiting review, contracts awaiting signature, change orders awaiting approval.",
      },
      {
        label: "Billing",
        title: "Invoices and payments",
        copy: "Pay open invoices online, see payment history and download receipts.",
      },
      {
        label: "Work",
        title: "Projects and boards",
        copy: "Shared project and board portals with milestones, files and comments, at the access level you set.",
      },
      {
        label: "Brand",
        title: "Layout and branding",
        copy: "Single column, two columns or tabs, with your logo and brand color.",
      },
    ],
    examplesTitle: "How a client uses the portal.",
    examplesIntro:
      "This example shows how a client portal moves through connected client work. It is a product workflow, not a promised business outcome.",
    examples: [
      {
        name: "Pay, Review and Sign",
        status: "Active",
        tag: "Portal",
        steps: [
          { tone: "trigger", text: "The client signs in with an email code" },
          { tone: "step", text: "They pay the invoice that is due" },
          { tone: "condition", text: "A proposal is waiting for their review" },
          { tone: "email", text: "They sign the contract from the signing link" },
          { tone: "task", text: "Outstanding drops to nothing" },
        ],
      },
    ],
    sidebarTitle: "Share your first portal",
    sidebarCopy: "Start a free trial and give a client one link to everything they need to act on.",
    image: "/home/screens/crm-light-clean.webp",
  },
  schedule: {
    eyebrow: "Projects & Calendar",
    title: "Plan the work, see the team's day, and let clients book",
    metaDescription:
      "A team calendar with a column per person, projects with budgets and hours, task boards with a Gantt view, and online booking pages from Specialist.",
    intro:
      "Schedule the team on one calendar, run each project on its own task board, and from Voyager see what every project has used against its budget and estimated hours. On Specialist, clients can book you online.",
    points: [
      "A Team view of the day with a column for each person.",
      "Projects with budgets, estimated hours and progress from their tasks.",
      "Kanban boards on every plan, the Gantt view and dependencies from Voyager.",
    ],
    connects:
      "Projects and events connect to clients, tasks, time tracking, invoices, proposals, expenses, files and the client portal.",
    examplesTitle: "Planning routines teams repeat.",
    examplesIntro:
      "These examples show how planning moves through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "New Project Kickoff",
        status: "Active",
        tag: "Projects",
        steps: [
          { tone: "trigger", text: "Create the project with budget and estimated hours" },
          { tone: "step", text: "Add tasks to its board with due dates" },
          { tone: "step", text: "Link tasks with Blocked by on the Gantt" },
          { tone: "task", text: "Schedule the kickoff call with the client" },
        ],
      },
      {
        name: "Monday Team Plan",
        status: "Active",
        tag: "Calendar",
        steps: [
          { tone: "trigger", text: "Open the calendar in Team view" },
          { tone: "condition", text: "A job is still in Unassigned" },
          { tone: "step", text: "Drag it onto a free team member" },
          { tone: "task", text: "If they connected Google, it lands on their Google Calendar" },
        ],
      },
    ],
    sidebarTitle: "Plan the week in one place",
    sidebarCopy: "Start a free trial and open the Team view on your own calendar.",
  },
  // Booking pages (Specialist and up). Facts: handoffs/2026-10-21-web-design-lead/review/booking/FACTS.md.
  "online-booking": {
    eyebrow: "Online Booking",
    title: "Online booking that does the admin before the client arrives",
    metaDescription:
      "Clients pick a free time, answer your intake questions and pay a deposit or the full price when they book. Online booking pages start on Specialist.",
    intro:
      "Share one booking page for your services. Clients see only the times your team is free, answer the questions you set and pay a deposit if you ask for one, and every booking lands on your calendar, the client record and, when paid, an invoice.",
    points: [
      "Only free times show, with Google busy time blocked.",
      "Intake questions and a deposit or the full price at booking.",
      "Each booking on the calendar, the client record and an invoice.",
    ],
    connects:
      "Bookings connect to clients, the team calendar, Google Calendar and Meet, invoices, Stripe payments, reminders and workflows.",
    capabilitiesTitle: "Set the page up the way you work.",
    capabilitiesIntro:
      "Each booking page has its own services, rules, hosts and look, and you can run several pages from one workspace.",
    capabilities: [
      {
        label: "Reminders",
        title: "Reminders before every booking",
        copy: "Clients get email reminders 24 hours and 1 hour before by default. Text reminders need Voyager and the client's phone number.",
      },
      {
        label: "Share",
        title: "A link, a QR code or your website",
        copy: "Copy the page link, share a QR code, or embed the page on your site as an inline form or a popup button. Clients don't need an account.",
      },
      {
        label: "Manage",
        title: "Clients can reschedule or cancel",
        copy: "The confirmation email carries a Manage booking link, and every change shows on the calendar and in your bookings list.",
      },
    ],
    examplesTitle: "Booking routines teams set up once.",
    examplesIntro:
      "These examples show how a booking moves through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Paid Consultation",
        status: "Active",
        tag: "Bookings",
        steps: [
          { tone: "trigger", text: "A client books a strategy session" },
          { tone: "step", text: "They answer the intake questions" },
          { tone: "step", text: "They pay the deposit through Stripe Checkout" },
          { tone: "task", text: "The invoice shows the deposit and the balance" },
        ],
      },
      {
        name: "New Booking Follow-Up",
        status: "Active",
        tag: "Workflows",
        steps: [
          { tone: "trigger", text: "A booking is created on the calendar" },
          { tone: "step", text: "Email the client how to prepare" },
          { tone: "task", text: "Create a prep task for the host" },
        ],
      },
    ],
    showcaseTitle: "Set the rules once for each page.",
    showcaseIntro:
      "Each booking page decides how bookings are confirmed and when clients can book. Times still follow each team member's working hours.",
    showcase: [
      {
        eyebrow: "Booking rules",
        title: "Instant booking or approve each request",
        copy: "Instant booking confirms the time straight away. With Approve each request you accept or decline, and the time can be held while you decide, from 1 to 168 hours (48 by default). A service can also wait for approval only on the days you pick, and the booking page tags each time Instant or Request.",
        mode: "workflow",
        label: "Booking rules",
      },
      {
        eyebrow: "Verify client email",
        title: "Only real email addresses",
        copy: "Turn on Verify client email and clients confirm a code before they can book.",
        mode: "records",
        label: "Booking rules",
      },
      {
        eyebrow: "Timing",
        title: "Notice, buffers and how far ahead",
        copy: "Set the minimum notice, a buffer after each booking and how many days ahead clients can book, or let each service use its own default.",
        mode: "timeline",
        label: "Booking rules",
      },
    ],
    sidebarTitle: "Take your first booking",
    sidebarCopy: "Booking pages start on Specialist. Set one up in four steps.",
  },
  communications: {
    eyebrow: "Inbox & Communications",
    title: "One inbox for every client email, text and call",
    metaDescription:
      "One shared inbox for email, SMS, calls and voicemail, matched to each client record. Assign conversations, reply now or later, and turn messages into tasks.",
    intro:
      "Email, texts, missed calls and voicemail land in one inbox, matched to the client who sent them. Assign the conversation, reply on their channel or schedule it, and turn any message into a task.",
    points: [
      "Email, SMS, calls and voicemail in one list, matched to the client.",
      "Assign conversations, leave internal notes and schedule replies.",
      "Turn any message into a note or a task, and set a reminder on the conversation.",
    ],
    connects:
      "Messages connect back to clients, requests, projects, tasks, automations, and sales workflows.",
    capabilitiesTitle: "One layer for every client conversation.",
    capabilitiesIntro:
      "Communications in Workspace369 keeps email, SMS, calls, and voicemail attached to the client record they belong to.",
    capabilities: [
      {
        label: "Inbox",
        title: "Shared inbox",
        copy: "Manage email and SMS alongside client and project records in one shared workspace.",
      },
      {
        label: "Voice",
        title: "Calling and voicemail",
        copy: "Route calls, track voicemail, and keep call history visible on the client record.",
      },
      {
        label: "SMS",
        title: "SMS and MMS messaging",
        copy: "Continue conversations through SMS and MMS with templates and scheduled follow-up.",
      },
      {
        label: "Context",
        title: "Shared client history",
        copy: "Review the client, recent messages, projects, and invoices before responding.",
      },
      {
        label: "Actions",
        title: "Work from a message",
        copy: "Create notes, tasks, reminders, and requests directly from the conversation.",
      },
    ],
    examplesTitle: "Communication workflows teams repeat.",
    examplesIntro:
      "These examples show how conversations move through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Missed-Call Text Back",
        status: "Active",
        tag: "Voice",
        steps: [
          { tone: "trigger", text: "Missed call lands on the client record" },
          { tone: "sms", text: "Automatic SMS reply goes to the caller" },
          { tone: "step", text: "Conversation continues in the shared inbox" },
          { tone: "condition", text: "Review client history before responding" },
          { tone: "task", text: "Create a follow-up task from the conversation" },
        ],
      },
      {
        name: "Reply and Scheduled Follow-Up",
        status: "Active",
        tag: "Follow-up",
        steps: [
          { tone: "trigger", text: "Client message arrives in the shared inbox" },
          { tone: "step", text: "Review client, project, and invoice context" },
          { tone: "step", text: "Reply with a saved email or SMS template" },
          { tone: "step", text: "Schedule the follow-up message for later" },
          { tone: "task", text: "Log a task to keep follow-up accountable" },
        ],
      },
    ],
    sidebarTitle: "Answer every client from one place",
    sidebarCopy: "Connect a mailbox in your free trial and see each conversation on its client.",
    image: "/images/settings-gear-icon.png",
  },
  invoices: {
    eyebrow: "Invoices",
    title: "Invoices clients pay online, with reminders that stop when they pay",
    metaDescription:
      "CRM and billing in one place: invoice from the client record and your catalog, get paid by card or bank through your own Stripe account, chase late invoices.",
    intro:
      "Your CRM and billing share one client record: each invoice starts from it and your catalog. Send it by email (or text, from Voyager), see when it is viewed and paid, and a reminder Workflow you switch on chases it if it runs late.",
    points: [
      "Build invoices from your saved products and services.",
      "Clients pay online by card or bank transfer through your Stripe account.",
      "See Open, Partial, Past due and Paid at a glance.",
    ],
    connects:
      "Billing connects with payments, expenses, accounting, products, inventory, projects, and reports.",
    capabilitiesTitle: "From first proposal to final invoice.",
    capabilitiesIntro:
      "Billing in Workspace369 keeps proposals, invoices, and retainers attached to the client and project history behind them.",
    capabilities: [
      {
        label: "Proposals",
        title: "Proposals",
        copy: "Build proposals from the client and catalog without retyping details.",
      },
      {
        label: "Invoices",
        title: "Invoices and status",
        copy: "Create invoices and track status attached to client and project history.",
      },
      {
        label: "Retainers",
        title: "Retainers and deposits",
        copy: "Manage retainers and deposits, and send recurring invoices for ongoing work.",
      },
      {
        label: "Catalog",
        title: "Catalog line items",
        copy: "Use product and service catalog items for faster, cleaner billing.",
      },
    ],
    examplesTitle: "Billing flows teams repeat.",
    examplesIntro:
      "These examples show how billing activity moves through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Proposal to Invoice",
        status: "Active",
        tag: "Conversion",
        steps: [
          { tone: "trigger", text: "Build a proposal from the client and catalog" },
          { tone: "email", text: "Send the proposal to the client" },
          { tone: "condition", text: "Client accepts the scope" },
          { tone: "step", text: "Carry the details into an invoice" },
          { tone: "task", text: "Track the invoice beside project history" },
        ],
      },
    ],
    sidebarTitle: "Send your first invoice",
    sidebarCopy: "Start a free trial and bill a client from your own catalog.",
    image: "/home/screens/invoice-list-light-clean.webp",
  },
  payments: {
    eyebrow: "Payments",
    title: "Online payments that land in your own Stripe account",
    metaDescription:
      "Take card, Apple Pay, Google Pay and bank payments on invoices and payment links, straight to your own Stripe account.",
    intro:
      "Clients tap Pay Now on the invoice and pay by card, wallet or bank. The money goes to your own Stripe account, and the invoice updates when the payment is confirmed.",
    points: [
      "Cards are always on; add Apple Pay, Google Pay, bank payments and pay-over-time options.",
      "Payment links and Flash Pay QR codes take a payment on the spot.",
      "Paid invoices update automatically.",
    ],
    connects:
      "Payments connect to invoices, proposals, retainers, payment links and Flash Pay.",
    capabilitiesTitle: "Every way to get paid, on one record.",
    capabilitiesIntro:
      "Payments in Workspace369 run through your own Stripe account and stay on the invoice they pay.",
    capabilities: [
      {
        label: "Methods",
        title: "Cards, wallets and bank",
        copy: "Cards are always on; add Apple Pay, Google Pay, bank payments (ACH) and pay-over-time options.",
      },
      {
        label: "Links",
        title: "Payment links",
        copy: "Send a link for the full balance, one installment or a custom amount.",
      },
      {
        label: "Deposits",
        title: "Deposits",
        copy: "Ask for a deposit that the client pays by card when they accept a proposal.",
      },
      {
        label: "Plans",
        title: "Payment plans",
        copy: "Split an invoice into scheduled installments the client pays from the same link.",
      },
      {
        label: "Flash Pay",
        title: "Flash Pay",
        copy: "Take a payment on the spot by QR code or link; each one becomes a paid invoice.",
      },
    ],
    examplesTitle: "How a deposit moves through the work.",
    examplesIntro:
      "This example shows how a payment moves through connected client work. It is a product workflow, not a promised business outcome.",
    examples: [
      {
        name: "Deposit on Acceptance",
        status: "Active",
        tag: "Deposits",
        steps: [
          { tone: "trigger", text: "The client accepts the proposal online" },
          { tone: "step", text: "They pay the deposit by card on the same page" },
          { tone: "step", text: "The payment lands in your Stripe account" },
          { tone: "step", text: "The deposit shows as paid on the proposal" },
          { tone: "task", text: "Kick off the work" },
        ],
      },
    ],
    sidebarTitle: "Take your first online payment",
    sidebarCopy: "Start a free trial, connect Stripe and send a client an invoice they can pay online.",
    image: "/images/release-notes/2026-10-17-more-ways-for-clients-to-pay/payment-methods.webp",
  },
  expenses: {
    eyebrow: "Expenses",
    title: "Scan the receipt, check it, and the cost lands on the project",
    metaTitle: "Expense tracking with receipt scan for client work",
    metaDescription:
      "Expense tracking: scan a receipt and AI fills in the expense, line items and tax. Link it to a project, post it to your books automatically. From Specialist.",
    intro:
      "Scan a receipt and AI fills in the vendor, amount, line items and tax. Check it, link the project and save.",
    points: [
      "Photo, PDF or HEIC, or shared from your iPhone.",
      "Filter by expense type to see where money went.",
      "Each cost counted against its job and in your books.",
    ],
    connects:
      "Expenses connect to projects, expense types, attachments, the accounting ledger, Home's Money widget and the Job Profitability report.",
    examplesTitle: "Expense routines teams repeat.",
    examplesIntro:
      "These examples show how expenses move through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Receipt to Project Cost",
        status: "Active",
        tag: "Receipts",
        steps: [
          { tone: "trigger", text: "Choose Add Expense, then Scan invoice/receipt" },
          { tone: "step", text: "AI fills in the form from the receipt" },
          { tone: "condition", text: "AI marks the scan low confidence" },
          { tone: "step", text: "Check it and link the project" },
          { tone: "task", text: "Save; the receipt stays attached" },
        ],
      },
      {
        name: "Month-End Cost Check",
        status: "Active",
        tag: "Profitability",
        steps: [
          { tone: "trigger", text: "Filter Expenses by Expense Type" },
          { tone: "step", text: "Select costs with no project linked" },
          { tone: "step", text: "Choose Link to Project" },
          { tone: "task", text: "Open Job Profitability for the month" },
        ],
      },
    ],
    faq: [
      {
        question: "Can Workspace369 read a receipt for me?",
        answer:
          "Yes. Choose Scan invoice/receipt and upload a photo, PDF or HEIC file. AI fills in the name, amount, date, description, line items and tax, and tells you when it is unsure of the scan. You review the form before saving. Each scan uses your plan's monthly AI usage.",
      },
      {
        question: "Can I add a receipt from my iPhone?",
        answer:
          "Yes. Share the receipt to Workspace369 from the iPhone share sheet and choose to create an expense. AI reads it and prefills the expense for you.",
      },
      {
        question: "Can I track expenses by project?",
        answer:
          "Yes. Link an expense to a project from the form, or select several in the list and choose Link to Project. Linked expenses count as direct costs in the Job Profitability report.",
      },
      {
        question: "Do expenses go into my accounting?",
        answer:
          "Yes, once accounting is set up. Saving an expense creates a journal entry against the expense's account and your cash account. New entries wait for review unless you turn off Require review before posting in Accounting Settings.",
      },
      {
        question: "Can I bill an expense back to a client?",
        answer:
          "No. Expenses are linked to a project and count in its profitability, but they aren't marked billable or added to invoices. To pass a cost on to a client, add it as a line on their invoice.",
      },
      {
        question: "Which plan includes expenses?",
        answer:
          "Expense tracking and receipt capture start at Specialist ($149/month), along with project and customer profitability. Manual journal entries, the review queue and vendor bills come with Commander.",
      },
    ],
    relatedLinks: [
      {
        label: "Google Workspace expense management guide",
        path: "/blog/google-workspace-expense-management",
      },
      {
        label: "Track billable expenses",
        path: "/blog/billable-expense-tracking",
      },
    ],
    sidebarTitle: "Know what each job costs",
    sidebarCopy: "Start a free trial and see where each project's money went.",
    ctaCopy: "Expenses start on Specialist: scan a receipt, link it to the job and see the margin.",
  },
  "ai-chat": {
    eyebrow: "AI AutoPilot",
    title: "The AI assistant that does the client busywork and asks before it sends",
    metaTitle: "Workspace AI and knowledge search",
    metaDescription:
      "Ask AI AutoPilot about clients, invoices and projects. It drafts and updates records, writes and translates replies, and waits for you before clients see it.",
    intro:
      "Ask AI AutoPilot about clients, invoices, projects and your calendar. It answers from your workspace, drafts and updates the records, and waits for your approval before anything goes to a client.",
    points: [
      "Ask in plain language and get answers from your own records.",
      "It drafts and updates records; sends to clients always wait for you.",
      "Compose, rewrite, translate and summarize, with AI usage on every plan.",
    ],
    connects:
      "AI connects with communication, knowledge base content, notes, automations, and workspace data.",
    capabilitiesTitle: "AI where the context already lives.",
    capabilitiesIntro:
      "Workspace AI combines chat, knowledge search, writing, translation, and summaries with connected workspace context.",
    capabilities: [
      {
        label: "Ask AI",
        title: "AI chat",
        copy: "Ask questions in chat and pull operational context from workspace data.",
      },
      {
        label: "Search",
        title: "Knowledge search",
        copy: "Use semantic search to find answers across the shared knowledge base.",
      },
      {
        label: "Write",
        title: "Compose and rewrite",
        copy: "Compose and rewrite client-facing messages inside the communication flow.",
      },
      {
        label: "Translate",
        title: "Translation",
        copy: "Translate client communication while keeping the draft close to the record.",
      },
      {
        label: "Summaries",
        title: "Summaries",
        copy: "Summarize long context into a useful next step for the team.",
      },
    ],
    examplesTitle: "AI workflows teams repeat.",
    examplesIntro:
      "These examples show how AI assists work already happening in the workspace. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Draft a Client Reply",
        status: "Active",
        tag: "Writing",
        steps: [
          { tone: "trigger", text: "Client message needs a considered reply" },
          { tone: "step", text: "Compose a draft with AI in the thread" },
          { tone: "step", text: "Rewrite the draft for tone and clarity" },
          { tone: "condition", text: "Review the result before sending" },
          { tone: "email", text: "Send the approved reply to the client" },
        ],
      },
      {
        name: "Find and Keep an Answer",
        status: "Active",
        tag: "Knowledge",
        steps: [
          { tone: "trigger", text: "Question comes up about a client or process" },
          { tone: "step", text: "Ask AI with workspace context attached" },
          { tone: "condition", text: "Check the answer against knowledge search results" },
          { tone: "step", text: "Summarize the relevant details for the team" },
          { tone: "task", text: "Save the result as a note on the record" },
        ],
      },
    ],
    sidebarTitle: "Hand off the busywork",
    sidebarCopy: "Start a free trial and ask AI AutoPilot about your own workspace.",
    image: "/images/feature-analytics.jpg",
  },
  autopilot: {
    eyebrow: "AI AutoPilot Today",
    title: "Open the app to a list of what needs you, in the order to do it",
    metaTitle: "AI AutoPilot Today: daily briefing and Client Health",
    metaDescription:
      "AI AutoPilot ranks what needs you today, flags clients and invoices that need attention, and drafts reminders for you to send. It only suggests.",
    intro:
      "AI AutoPilot reads your invoices, projects, requests, tasks and schedule, then lists what needs you today: an invoice to chase, a client to check in with, a request to answer. Each item says why it is there, and nothing is sent for you.",
    points: [
      "Today and the watches on money, projects and tasks on every plan.",
      "An AI-written morning briefing and message sorting from Voyager.",
      "It drafts the reminder or follow-up; you review it and press Send.",
    ],
    connects:
      "AutoPilot Today reads the invoices, payment plans, projects, tasks, requests, calendar and client messages already in Workspace369.",
    sidebarTitle: "Start the day with a list",
    sidebarCopy: "Start a free trial and open AI AutoPilot Today on your own workspace.",
    ctaTitle: "See what needs you before you open your inbox.",
    ctaCopy: "Start free, turn on Today in AutoPilot settings, and let it sort the day while you do the work.",
    image: "/images/feature-analytics.jpg",
  },
  analytics: {
    eyebrow: "Dashboards & Reports",
    title: "See what needs you and where the money went",
    metaTitle: "Dashboards and reporting",
    metaDescription:
      "A Home that shows overdue invoices, proposals waiting and money coming in, plus AR aging and job profitability reports built from the work you already track.",
    intro:
      "Home sums up what's overdue, waiting on a reply and due in, from work your team already records. Reports show who owes you and which work pays.",
    points: [
      "Home and Needs your attention on every plan",
      "A daily AI briefing from Voyager",
      "AR aging and profitability reports from Specialist",
    ],
    connects:
      "Home and the reports read the invoices, payments, proposals, contracts, expenses, time entries, tasks and inventory already in Workspace369.",
    examplesTitle: "Reporting routines teams repeat.",
    examplesIntro:
      "These examples show how teams use Home and the reports in a normal week. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Monday Money Check",
        status: "Active",
        tag: "Collections",
        steps: [
          { tone: "trigger", text: "Open Home on Monday morning" },
          { tone: "step", text: "Read the briefing and Coming in" },
          { tone: "condition", text: "Overdue invoices under Needs your attention" },
          { tone: "step", text: "Choose Send reminders" },
          { tone: "task", text: "Review each reminder before it goes" },
        ],
      },
      {
        name: "Month-End Project Review",
        status: "Active",
        tag: "Profitability",
        steps: [
          { tone: "trigger", text: "Open Job Profitability" },
          { tone: "step", text: "Set the report period to last month" },
          { tone: "step", text: "Sort projects by margin" },
          { tone: "condition", text: "A project's margin drops" },
          { tone: "task", text: "Open its invoices and linked expenses" },
        ],
      },
    ],
    sidebarTitle: "Home comes with every plan.",
    sidebarCopy: "Start a free trial and see your own overdue, waiting and coming-in numbers.",
    image: "/images/feature-analytics.jpg",
  },
  files: {
    eyebrow: "Storage & Files",
    title: "Files linked to the clients and projects they belong to",
    metaDescription:
      "Keep client files in folders linked to clients, projects, invoices and tasks, with version history, team roles and share links that can need a password.",
    intro:
      "Link each file to its client, project, invoice or task, share it with a link, and restore any older version.",
    points: [
      "Link one file to several clients, projects, invoices or tasks.",
      "Share links that can view or download, or need a password.",
      "Version history and Recently deleted, on every plan.",
    ],
    connects:
      "Files connect to clients, projects, invoices, tasks and signed contracts, and reach people outside the workspace through share links.",
    examplesTitle: "File routines teams repeat.",
    examplesIntro:
      "These examples show how files move through client work in Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Deliver Final Files",
        status: "Active",
        tag: "Delivery",
        steps: [
          { tone: "trigger", text: "Upload the final logo files to the client folder" },
          { tone: "step", text: "Link them to the project" },
          { tone: "step", text: "Share with a link that can download" },
          { tone: "condition", text: "Require a password" },
          { tone: "task", text: "Send the link to the client" },
        ],
      },
      {
        name: "Undo a Bad Upload",
        status: "Active",
        tag: "Versions",
        steps: [
          { tone: "trigger", text: "A teammate uploads the wrong draft" },
          { tone: "step", text: "Open the file and its Version history" },
          { tone: "condition", text: "Preview the earlier version" },
          { tone: "task", text: "Restore this version" },
        ],
      },
    ],
    sidebarTitle: "Keep files with the work",
    sidebarCopy: "Bring one client folder in and link it on day one.",
    image: "/images/feature-chips-illustration.png",
  },
  products: {
    eyebrow: "Services & Products",
    title: "One catalog for your services, products and stock",
    metaDescription:
      "Keep services, digital items and physical products with prices, costs and margins in one catalog for proposals and invoices. Inventory from Specialist.",
    intro:
      "Services, digital items and physical products with their price, cost and margin, so proposals and invoices start from the same lines. From Specialist, billing a product takes it off the stock.",
    points: [
      "Services, digital items and physical products, priced per hour, per unit or as a flat rate.",
      "Price, cost, profit and gross margin on every item.",
      "Stock counts, adjustments and low-stock alerts from Specialist.",
    ],
    connects:
      "Catalog items feed proposals and invoices, and tracked products record each stock movement against the invoice that used them.",
    examplesTitle: "Catalog routines teams repeat.",
    examplesIntro:
      "These examples show how the catalog moves through client work in Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Catalog Item to Invoice",
        status: "Active",
        tag: "Catalog",
        steps: [
          { tone: "trigger", text: "Add a service with its price and cost" },
          { tone: "step", text: "Pick it from the catalog on a proposal" },
          { tone: "step", text: "Convert the accepted proposal to an invoice" },
          { tone: "task", text: "The same line and price carry through" },
        ],
      },
      {
        name: "Restock a Low Item",
        status: "Active",
        tag: "Inventory",
        steps: [
          { tone: "trigger", text: "An invoice takes stock under the alert level" },
          { tone: "condition", text: "The product shows Low Stock" },
          { tone: "step", text: "Reorder from your supplier" },
          { tone: "step", text: "Adjust Stock / Usage: Add, reason Received" },
          { tone: "task", text: "The movement lands in the inventory history" },
        ],
      },
    ],
    sidebarTitle: "Standardize what you sell",
    sidebarCopy: "A catalog on every plan; inventory from Specialist.",
    image: "/images/pdf-customization.jpg",
  },
  support: {
    eyebrow: "Platform & Support",
    title: "Help inside the app, from a quick answer to a person",
    metaDescription:
      "Support in Workspace369: the Help Center, Live Chat with an AI first answer and a person when needed, tickets you can follow, and email support on every plan.",
    intro:
      "Look it up in the Help Center, ask Live Chat, where AI answers first and a person takes over, or file a ticket and follow it to the end.",
    points: [
      "Help Center, Live Chat, tickets and email support on every plan.",
      "Live Chat shows the support articles behind each AI answer.",
      "A dedicated support contact on Commander and Enterprise.",
    ],
    connects:
      "Support connects to the Help Center at docs.workspace369.com, your ticket history and your chat history, on the web and in the mobile app.",
    examplesTitle: "Support moments teams repeat.",
    examplesIntro:
      "These examples show how teams get help inside Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Quick Question in Live Chat",
        status: "Active",
        tag: "Live Chat",
        steps: [
          { tone: "trigger", text: "Open Support and start a chat" },
          { tone: "step", text: "Workspace369 AI answers with its sources" },
          { tone: "condition", text: "Still stuck, or want a person" },
          { tone: "step", text: "The support team joins the chat" },
          { tone: "task", text: "Rate the chat when it ends" },
        ],
      },
      {
        name: "Report a Bug",
        status: "Active",
        tag: "Ticket",
        steps: [
          { tone: "trigger", text: "Spot something that looks wrong" },
          { tone: "step", text: "New Ticket: category Bug Report" },
          { tone: "step", text: "Set the priority and attach a screenshot" },
          { tone: "condition", text: "Support replies in the ticket" },
          { tone: "task", text: "Resolved, or reopen if it comes back" },
        ],
      },
    ],
    sidebarTitle: "Help when you need it",
    sidebarCopy: "Help Center, Live Chat and tickets come with every plan.",
    image: "/images/settings-gear-icon.png",
  },
  themes: {
    eyebrow: "Themes & Appearance",
    title: "Dark mode, accent colors and themes for every page you share",
    metaTitle: "Workspace themes and dark mode",
    metaDescription:
      "Pick light, dark or system, an accent color and text size in Workspace369, plus 35 themes for your client portal, booking page and shared links.",
    intro:
      "Each person picks light, dark or system, an accent color and a text size. The pages you share with clients have their own 35 themes, so they can match your brand.",
    points: [
      "Light, dark or system, with a searchable accent color, for each person.",
      "A different look per workspace when you work in more than one.",
      "35 themes for the client portal, booking page, board links and invoice links.",
    ],
    connects:
      "Themes connect to your client portal look, booking pages, shared task boards and invoice links.",
    examplesTitle: "Theme changes teams make.",
    examplesIntro:
      "These examples show how people set up Appearance and client-facing themes. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Dark Mode for Late Work",
        status: "Active",
        tag: "Appearance",
        steps: [
          { tone: "trigger", text: "Open Settings, then Appearance" },
          { tone: "step", text: "Pick the Dark tile" },
          { tone: "step", text: "Search the accent list for green" },
          { tone: "condition", text: "Applies to: Only this workspace" },
          { tone: "task", text: "Save from the bar at the bottom" },
        ],
      },
      {
        name: "Theme a Client Board Link",
        status: "Active",
        tag: "Shared boards",
        steps: [
          { tone: "trigger", text: "Open the board's Public appearance" },
          { tone: "step", text: "Theme colors start on Inherit (portal theme)" },
          { tone: "condition", text: "This client's board needs its own theme" },
          { tone: "step", text: "Pick Night from the 35 themes" },
          { tone: "task", text: "Share the board link" },
        ],
      },
    ],
    sidebarTitle: "Make it yours",
    sidebarCopy: "Start a free trial and switch on dark mode in Settings.",
    image: "/images/feature-themes.jpg",
  },
  branding: {
    eyebrow: "Branding",
    title: "Your logo and colors on everything clients see",
    metaTitle: "Branded client portal, booking pages and emails",
    metaDescription:
      "Upload your logo and Workspace369 builds three looks from its colors for your portal and booking page, then brands your emails, invoices and PDFs.",
    intro:
      "Upload your logo and Workspace369 builds three looks from its colors for your client portal and booking page. The same logo and color go on your emails, invoice links and PDFs.",
    points: [
      "Three looks built from your logo's colors, or eight ready-made looks.",
      "A brand color menu that warns you when buttons or links get hard to read.",
      "Branded emails, invoice links and PDFs, on every plan.",
    ],
    connects:
      "Branding connects to the client portal, booking pages, Email appearance, Document themes and the invoice PDF template.",
    examplesTitle: "Branding jobs teams repeat.",
    examplesIntro:
      "These examples show how a business sets up its brand in Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Brand a New Workspace",
        status: "Active",
        tag: "Brand & looks",
        steps: [
          { tone: "trigger", text: "Open Settings, then Brand & looks" },
          { tone: "step", text: "Upload logo and let it find the colors" },
          { tone: "step", text: "Use one of the three logo looks" },
          { tone: "condition", text: "Check the booking page preview" },
          { tone: "task", text: "Fine-tune one part, keep the rest" },
        ],
      },
      {
        name: "Match Invoice Emails to the Brand",
        status: "Active",
        tag: "Email appearance",
        steps: [
          { tone: "trigger", text: "Open Email appearance" },
          { tone: "step", text: "Pick the Branded Band layout" },
          { tone: "step", text: "Set the brand color and email footer" },
          { tone: "condition", text: "Logo position: Top or Signature" },
          { tone: "email", text: "Send the next invoice in the new look" },
        ],
      },
    ],
    sidebarTitle: "Look like yourself",
    sidebarCopy: "Start a free trial and build looks from your own logo.",
    image: "/images/feature-chips-illustration.png",
  },
  "pdf-customization": {
    eyebrow: "Documents",
    title: "Invoice PDFs, invoice pages and proposals in your own design",
    metaTitle: "Custom invoice PDF and proposal templates",
    metaDescription:
      "Choose from 6 invoice PDF templates, 10 invoice page layouts in 35 palettes and 4 proposal themes, and pick what each PDF shows, with a live preview.",
    intro:
      "Choose what your invoice PDF shows and how it looks, with a preview that follows every change. Invoice links and proposals get designs of their own.",
    points: [
      "Six invoice PDF templates, your theme color, logo size and alignment.",
      "Show or hide each detail, from PO numbers to a tax summary by rate.",
      "Ten invoice page layouts in 35 palettes, and four proposal themes.",
    ],
    connects:
      "Document design connects to invoices, invoice emails, the online invoice page, proposals, tax codes and your workspace logo.",
    examplesTitle: "Document setups teams repeat.",
    examplesIntro:
      "These examples show how businesses set up their documents in Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Invoice PDF for a VAT-Registered Business",
        status: "Active",
        tag: "PDF template",
        steps: [
          { tone: "trigger", text: "Open Settings, Invoices, PDF template" },
          { tone: "step", text: "Switch on Per-line tax column and Tax summary by rate" },
          { tone: "step", text: "Add registration numbers and a footer line" },
          { tone: "condition", text: "Check the PDF preview" },
          { tone: "task", text: "Save, and every invoice PDF follows" },
        ],
      },
      {
        name: "A Client That Needs a PO",
        status: "Active",
        tag: "Purchase orders",
        steps: [
          { tone: "trigger", text: "Client asks for their PO on invoices" },
          { tone: "step", text: "Switch on PO number in Purchase orders" },
          { tone: "step", text: "Add the PO to the invoice" },
          { tone: "email", text: "Send the invoice with the PDF attached" },
          { tone: "task", text: "The client matches it to their order" },
        ],
      },
    ],
    sidebarTitle: "Send documents that look like you",
    sidebarCopy: "Start a free trial and set up your invoice PDF in minutes.",
    image: "/images/pdf-customization.jpg",
  },
  quoting: {
    eyebrow: "Proposals",
    title: "Proposals clients accept, sign and pay from one link",
    metaDescription:
      "Build proposals from your catalog with optional add-ons, see which sections clients read, and let them accept, sign and pay the deposit from one link.",
    intro:
      "Write the proposal once from your catalog and send one link. The client picks any add-ons, signs, pays the deposit and moves straight on to the contract, and you see every step.",
    points: [
      "Clients ask questions on the proposal, and you answer from the app.",
      "An accepted proposal becomes the invoice without retyping.",
      "Deposits are paid by card through your own Stripe account.",
    ],
    connects:
      "Proposals connect to the catalog, contracts, payments, invoices and change orders.",
    capabilitiesTitle: "From first draft to paid deposit.",
    capabilitiesIntro:
      "A proposal in Workspace369 is a page the client can act on, built from the records you already keep.",
    capabilities: [
      {
        label: "Build",
        title: "Blocks and catalog items",
        copy: "Add text, timeline, terms and image blocks, and price the work from your catalog.",
      },
      {
        label: "Add-ons",
        title: "Optional line items",
        copy: "Mark items optional so the client can add them before accepting.",
      },
      {
        label: "Accept",
        title: "Signature and deposit",
        copy: "The client signs and pays the deposit by card when they accept.",
      },
      {
        label: "Insights",
        title: "Engagement tracking",
        copy: "See total opens, time spent and the most-read section.",
      },
      {
        label: "Invoice",
        title: "Convert to Invoice",
        copy: "Turn an accepted proposal into an invoice without retyping it.",
      },
    ],
    examplesTitle: "How an accepted proposal moves on.",
    examplesIntro:
      "This example shows how a proposal moves through connected client work. It is a product workflow, not a promised business outcome.",
    examples: [
      {
        name: "Accept, Sign and Invoice",
        status: "Active",
        tag: "Handoff",
        steps: [
          { tone: "trigger", text: "The client accepts the proposal and pays the deposit" },
          { tone: "step", text: "They sign the attached contract right after" },
          { tone: "condition", text: "Scope changes after the deposit go through a change order" },
          { tone: "step", text: "Convert the proposal to an invoice" },
          { tone: "task", text: "Kick off the work" },
        ],
      },
    ],
    sidebarTitle: "Send your first proposal",
    sidebarCopy: "Start a free trial and send a proposal your client can accept, sign and pay.",
    image: "/images/release-notes/2026-10-04-proposal-builder/proposal-editor.webp",
  },
  contracts: {
    eyebrow: "Contracts",
    title: "Send contracts for e-signature and keep a signing record",
    metaTitle: "Contract software for freelancers and agencies",
    metaDescription:
      "Send client contracts for e-signature with email code checks, a hash-chained audit trail, a certificate of completion, countersigning, and a draft invoice.",
    intro:
      "Write the contract once, choose who signs and in what order, and send it. Clients sign online or on your device, every step is logged, and the signed copy is sealed and sent to everyone.",
    points: [
      "Signers go in the order you set, or all at once.",
      "Reminders go to whoever's turn it is.",
      "Attach it to a proposal and the client signs right after accepting.",
    ],
    connects:
      "Contracts connect to clients, proposals, templates, reminders and invoices, with the audit record behind each signature.",
    capabilitiesTitle: "From sent to signed to billed.",
    capabilitiesIntro:
      "Contracts in Workspace369 keep the signing steps, the evidence, and the next billing step in one place.",
    capabilities: [
      {
        label: "Verify",
        title: "Email code",
        copy: "The signer enters a 6-digit code we email them before they can open the document. Recommended for most contracts.",
      },
      {
        label: "Audit",
        title: "Tamper-evident audit trail",
        copy: "Each signing event is chained to the one before it with SHA-256 hashes, so a change to the record shows.",
      },
      {
        label: "Certificate",
        title: "Certificate of completion",
        copy: "Get a certificate of completion PDF once everyone has signed.",
      },
      {
        label: "In person",
        title: "Sign on a device",
        copy: "Hand your phone or tablet to a client and collect the signature in the room.",
      },
      {
        label: "Bulk",
        title: "Bulk send",
        copy: "Send one template to up to 500 people from your clients or a CSV, each with their own contract to sign.",
      },
      {
        label: "Follow-up",
        title: "Reminders, expiry, and countersigning",
        copy: "Remind signers who have not signed, set an expiry date, and countersign when your own signature is needed.",
      },
    ],
    workflowTitle: "Send, sign, lock, and bill.",
    workflow: [
      {
        title: "Write and send",
        copy: "Write your contract from your own template. Pick the client and send it for signature.",
      },
      {
        title: "Collect signatures",
        copy: "Signers confirm their email with a code and sign online or in person. Reminders go out until it is done.",
      },
      {
        title: "Seal the signed copy",
        copy: "When everyone has signed, the copy is sealed and sent to everyone. To change the terms later, duplicate the contract and send the new version.",
      },
      {
        title: "Create the invoice",
        copy: "Turn on Create an invoice and a draft invoice is made when everyone has signed, for you to review.",
      },
    ],
    examplesTitle: "Contract flows teams repeat.",
    examplesIntro:
      "These examples show how a contract moves through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Client Contract to Draft Invoice",
        status: "Active",
        tag: "E-signature",
        steps: [
          { tone: "trigger", text: "Send the contract to the client" },
          { tone: "condition", text: "Signer enters the email code" },
          { tone: "step", text: "Client signs and you countersign" },
          { tone: "email", text: "Certificate of completion is ready" },
          { tone: "task", text: "Review the draft invoice created from it" },
        ],
      },
      {
        name: "Bulk Send with Reminders",
        status: "Active",
        tag: "Bulk",
        steps: [
          { tone: "trigger", text: "Choose the contract and the signers" },
          { tone: "email", text: "Send to everyone in one go" },
          { tone: "condition", text: "Check who has not signed yet" },
          { tone: "email", text: "Reminders go to open signers" },
          { tone: "task", text: "Contract expires if it stays unsigned" },
        ],
      },
    ],
    sidebarTitle: "Sign, then bill",
    sidebarCopy: "Keep the signature and the invoice on the same record.",
    heroLabel: "Contract signing",
    heroMode: "workflow",
    showcaseTitle: "See how a contract moves from sent to signed.",
    ctaTitle: "Send your first contract from your workspace.",
  },
  "retainers-and-recurring-billing": {
    eyebrow: "Retainers & Recurring",
    title: "Retainers that count the hours and bill every cycle",
    metaTitle: "Retainer billing software with recurring autopay",
    metaDescription:
      "Run hour-bank, fixed monthly and prepaid retainers. Track approved hours against the bank, bill extras, and charge cards on autopay with consent.",
    intro:
      "Set up an hour bank, a fixed monthly fee or a prepaid deposit once. Workspace369 counts the hours, invoices every cycle and charges the card the client agreed to.",
    points: [
      "Hour bank, fixed monthly and prepaid deposit retainers.",
      "Approved time draws the hour bank down, with pending time shown before it goes over.",
      "Clients accept the terms and turn on card autopay when they pay the first invoice.",
    ],
    connects:
      "Retainers connect to clients, time entries, invoices, contracts, payments and the client portal.",
    capabilitiesTitle: "Everything for work that repeats.",
    capabilitiesIntro:
      "Retainer billing in Workspace369 keeps the allowance, the schedule and the payment method attached to the client.",
    capabilities: [
      {
        label: "Types",
        title: "Three retainer types",
        copy: "Hour bank, fixed monthly or prepaid deposit, each with its own terms.",
      },
      {
        label: "Hours",
        title: "Hour bank meter",
        copy: "See approved hours, time waiting for approval and what is left this cycle.",
      },
      {
        label: "Balance",
        title: "Prepaid drawdown and top-ups",
        copy: "Apply a prepaid balance to invoices and send a top-up when it runs low.",
      },
      {
        label: "Plans",
        title: "Recurring invoices",
        copy: "Repeat any invoice on a schedule, with or without autopay.",
      },
      {
        label: "Autopay",
        title: "Card autopay with consent",
        copy: "The client ticks one box on the first invoice to accept the terms and autopay.",
      },
      {
        label: "Notice",
        title: "Notice before every charge",
        copy: "The client is emailed 3 days before each automatic charge, 7 days for yearly plans.",
      },
    ],
    examplesTitle: "How a retainer month moves.",
    examplesIntro:
      "This example shows how an hour-bank retainer moves through connected client work. It is a product workflow, not a promised business outcome.",
    examples: [
      {
        name: "Monthly Hour Bank",
        status: "Active",
        tag: "Retainers",
        steps: [
          { tone: "trigger", text: "The client pays the first invoice and turns on autopay" },
          { tone: "step", text: "Time logged for the client counts toward the hour bank" },
          { tone: "condition", text: "Usage alerts at 80% and 100% of the bank" },
          { tone: "email", text: "The next invoice adds any extra hours" },
          { tone: "task", text: "Unused hours roll over up to the cap" },
        ],
      },
    ],
    sidebarTitle: "Bill on a schedule",
    sidebarCopy: "Hour banks, fixed monthly fees, prepaid deposits and recurring invoices come with every plan.",
    heroLabel: "Retainer and recurring billing",
    heroImageAlt: "Workspace369 invoice list showing paid, partial, and past due invoices with amounts and due dates",
    heroMode: "records",
    showcaseTitle: "See how retainers and recurring plans fit into billing.",
    ctaTitle: "Set up your first retainer or recurring plan.",
  },
  "change-orders": {
    eyebrow: "Change Orders",
    title: "Change orders that keep scope changes in writing",
    metaTitle: "Change order software for scope changes",
    metaDescription:
      "Change orders: record scope changes after a client accepts or pays, with locked originals, line-by-line changes, the new total and approval by email link.",
    intro:
      "When the client asks for more after they said yes, write it down. A change order shows each line that changed and what it does to the total, and nothing changes until the client approves it.",
    points: [
      "Accepted proposals and paid invoices lock, and the original stays on record.",
      "Each change order groups lines as Added, Removed or Changed and shows the new balance due.",
      "Clients approve from an email link. Change orders start on Specialist.",
    ],
    connects:
      "Change orders work on locked proposals and invoices, and update the balance, payment plan and invoice PDF of the document they change.",
    examplesTitle: "Change order routines teams repeat.",
    examplesIntro:
      "These examples show how a scope change moves through Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Extra Deliverable After Acceptance",
        status: "Active",
        tag: "Client request",
        steps: [
          { tone: "trigger", text: "Client asks for a landing page" },
          { tone: "step", text: "Open the locked proposal and add a change order" },
          { tone: "step", text: "Add the new item and write the reason" },
          { tone: "email", text: "Send to client for approval" },
          { tone: "task", text: "Approved: the new total and balance apply" },
        ],
      },
      {
        name: "Approval Given on a Call",
        status: "Active",
        tag: "Recorded approval",
        steps: [
          { tone: "trigger", text: "Client agrees to a price correction by phone" },
          { tone: "condition", text: "Client approval is not required in settings" },
          { tone: "step", text: "Choose Record client approval" },
          { tone: "step", text: "Note how the client approved" },
          { tone: "task", text: "The change applies and the note joins the audit trail" },
        ],
      },
    ],
    sidebarTitle: "Keep scope in writing",
    sidebarCopy: "Change orders start on Specialist. Show what changed, what it costs and who approved it.",
    ctaTitle: "Turn the next scope change into a change order.",
  },
  // Flash Pay (whiteboard approved by the 3rd-pass rule, Nov 4). Facts: the Square + membership
  // LEAD's WEB-PAGES-FACTS-2026-11-04 section 2. Never claim card readers, tap to pay, Register,
  // Square, an automatic receipt email, unconditional Apple Pay or Google Pay, ACH, an Android app
  // or tips in the iPhone app.
  "flash-pay": {
    eyebrow: "Flash Pay",
    title: "Take a QR code payment on the spot with Flash Pay",
    metaDescription:
      "Flash Pay takes a payment on the spot: type the amount, show a QR code or send a pay link, and each payment lands in your Stripe account as a paid invoice.",
    intro:
      "Get paid on the spot, with no invoice to write first. Type the amount and show a QR code, or send a link. The customer pays from their own phone, the money goes to your Stripe account, and the sale is already a paid invoice.",
    points: [
      "No details needed first: the customer adds their own when they pay.",
      "Every sale is a paid invoice in Workspace369, with nothing to retype.",
      "Turn on Save customers as clients and new ones are added, matched by email or phone first.",
    ],
    connects:
      "Flash Pay uses your Stripe connection and payment methods, and every payment becomes a paid invoice on the client's record.",
    workflowTitle: "Start taking payments in three steps.",
    workflow: [
      {
        title: "Connect Stripe",
        copy: "Connect Stripe when Flash Pay asks. The money goes straight to your own Stripe account.",
      },
      {
        title: "Type the amount",
        copy: "Open Flash Pay in the sidebar, just below Invoices, and type what the customer owes.",
      },
      {
        title: "Show the code or send the link",
        copy: "Choose Show QR code and turn the phone to your customer, or Send payment link by text, email or share. Waiting for payment turns into Payment received.",
      },
    ],
    capabilitiesTitle: "Everything Flash Pay does, on one screen.",
    capabilitiesIntro:
      "Flash Pay works with QR codes and pay links, not card readers or tap to pay. Every payment runs through your own Stripe account.",
    capabilities: [
      {
        label: "Two ways",
        title: "QR code or pay link",
        copy: "Show the code at the counter, or copy, share, text or email the link.",
      },
      {
        label: "Links",
        title: "Links that expire, or cancel one",
        copy: "Links expire after 1, 7, 30 or 90 days, and cancelling a link charges nothing.",
      },
      {
        label: "Customers",
        title: "Ask only what you need",
        copy: "Choose whether to ask for a name, email or phone, and save customers as clients.",
      },
      {
        label: "Today",
        title: "See the day at a glance",
        copy: "Each payment shows as Waiting, Paid, Cancelled or Expired, with Collected today at the top.",
      },
      {
        label: "Tips",
        title: "Tips on the web app",
        copy: "Suggest tip percentages, or fixed tips on small amounts, worked out before or after tax.",
      },
    ],
    showcaseTitle: "What you and your customer see.",
    showcaseIntro:
      "These are Flash Pay screens from the app. The business, amounts and customer are sample data.",
    showcase: [
      {
        title: "Show QR code",
        copy: "Turn the phone to your customer. They point their camera at the code and pay on their own phone.",
        image: "/images/features/flash-pay/flash-pay-qr-code.webp",
        imageAlt: "Flash Pay's QR screen in light and dark mode: Turn the phone to your customer, the business name with Scan to pay, a QR code and $120.00.",
        mode: "workflow",
        label: "Flash Pay",
      },
      {
        title: "Send payment link",
        copy: "Copy or share needs no customer details. They add their own when they pay, and the link expires when you choose.",
        image: "/images/features/flash-pay/flash-pay-send-payment-link.webp",
        imageAlt: "Send payment link: a 369.dev pay link with a Copy button, Text message, Email and Share, and Expires in 7 days.",
        mode: "workflow",
        label: "Flash Pay",
      },
      {
        title: "Payment received",
        copy: "The payment is booked as a paid invoice, and a new customer is saved as a client when you turn that on.",
        image: "/images/features/flash-pay/flash-pay-payment-received.webp",
        imageAlt: "Payment received in light and dark mode: $138.00 from Maya Chen, with her email and New client saved.",
        mode: "records",
        label: "Flash Pay",
      },
    ],
    examplesTitle: "Flash Pay in a working day.",
    examplesIntro:
      "These examples show the steps in Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Session Fee at the Shoot",
        status: "Active",
        tag: "QR code",
        steps: [
          { tone: "trigger", text: "The session ends and the client is ready to pay" },
          { tone: "step", text: "Type $250 in Flash Pay and choose Show QR code" },
          { tone: "step", text: "The client scans and pays on their own phone" },
          { tone: "task", text: "Payment received: a paid invoice is in Workspace369" },
        ],
      },
      {
        name: "Deposit After a First Meeting",
        status: "Active",
        tag: "Pay link",
        steps: [
          { tone: "trigger", text: "The client agrees to start" },
          { tone: "step", text: "Type the deposit and choose Send payment link" },
          { tone: "email", text: "Text the link to the client" },
          { tone: "task", text: "They pay and are saved as a new client" },
        ],
      },
      {
        name: "Drop-in at the Front Desk",
        status: "Active",
        tag: "Tips",
        steps: [
          { tone: "trigger", text: "A drop-in client finishes their class" },
          { tone: "condition", text: "Ask for a tip is on in Flash Pay settings" },
          { tone: "step", text: "Show the QR code for the class fee" },
          { tone: "task", text: "The client picks a tip and pays; Collected today goes up" },
        ],
      },
    ],
    sidebarTitle: "Take your first Flash Pay",
    sidebarCopy: "Start a free trial, connect Stripe and take a payment with a QR code or a pay link.",
  },
  "mobile-app": {
    eyebrow: "Mobile App",
    heroImageAlt: "App Store screenshots of the Workspace369 iPhone app: Home, a client page and the projects list",
    title: "A CRM and billing app for iPhone and iPad",
    metaTitle: "CRM app for iPhone and iPad for client work",
    metaDescription:
      "Run client work from the Workspace369 app for iPhone and iPad: invoices, proposals, time, calendar, inbox, clients, payment links, and receipt scans.",
    intro:
      "A native app that opens the same workspace as the web, so you can send an invoice or log time wherever you are.",
    points: [
      "Native app for iPhone and iPad, with iPad in every orientation.",
      "Same workspace as the web app at app.workspace369.com.",
      "Send invoices and proposals, track time, and share payment links.",
    ],
    connects:
      "The app connects to the same clients, invoices, proposals, time, calendar, and inbox as the web workspace.",
    capabilitiesTitle: "Your client work in your pocket.",
    capabilitiesIntro:
      "The app works on the same data as the web app, so nothing needs to be synced by hand.",
    capabilities: [
      {
        label: "Billing",
        title: "Invoices and proposals",
        copy: "Create and send invoices and proposals from your phone or tablet.",
      },
      {
        label: "Time",
        title: "Track time",
        copy: "Start a timer or add a time entry while you work.",
      },
      {
        label: "Schedule",
        title: "Calendar",
        copy: "See your calendar and what is coming up for the day.",
      },
      {
        label: "Clients",
        title: "Inbox and clients",
        copy: "Read the inbox and open any client record from the same app.",
      },
      {
        label: "Payments",
        title: "Payment links",
        copy: "Send a payment link so a client can pay an invoice from their own device.",
      },
      {
        label: "Receipts",
        title: "Scan receipts from the share sheet",
        copy: "Share a receipt to Workspace369 to start an expense. Expense tracking and receipt capture start at Specialist.",
      },
    ],
    workflowTitle: "Pick up where the desktop left off.",
    workflow: [
      {
        title: "Install from the App Store",
        copy: "Download Workspace369 on iPhone or iPad and sign in with your account.",
      },
      {
        title: "Open your workspace",
        copy: "You see the same workspace you use at app.workspace369.com.",
      },
      {
        title: "Do the quick work",
        copy: "Send an invoice, log time, check the calendar, or answer the inbox.",
      },
      {
        title: "Finish at the desk",
        copy: "Everything you did is already in the web app when you sit down.",
      },
    ],
    examplesTitle: "Mobile flows teams repeat.",
    examplesIntro:
      "These examples show how the app fits into a working day. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Invoice from the Job Site",
        status: "Active",
        tag: "Billing",
        steps: [
          { tone: "trigger", text: "Finish the work for a client" },
          { tone: "step", text: "Create the invoice on your phone" },
          { tone: "email", text: "Send the invoice with a payment link" },
          { tone: "task", text: "See the status on the web app later" },
        ],
      },
      {
        name: "Receipt to Expense",
        status: "Active",
        tag: "Receipts",
        steps: [
          { tone: "trigger", text: "Open a receipt on your phone" },
          { tone: "step", text: "Share it to Workspace369 from the share sheet" },
          { tone: "step", text: "Review the scanned expense" },
          { tone: "task", text: "Attach it to the client or project" },
        ],
      },
    ],
    sidebarTitle: "Run work from anywhere",
    sidebarCopy: "One workspace on web, iPhone, and iPad.",
    heroLabel: "iPhone and iPad app",
    heroMode: "records",
    showcaseTitle: "See what you can do from the app.",
    ctaTitle: "Download the app and open your workspace.",
  },
  "check-ins": {
    eyebrow: "Check-ins",
    title: "Check members in at the front desk, and see why when you can't",
    metaTitle: "Member Check-in Software for Gyms and Studios",
    metaDescription:
      "Check members in by camera, scanner or name search. See what's used and left, or exactly why a pass can't be used, with Undo and logged overrides.",
    intro:
      "Scan a member's pass with the camera or a scanner, or search by name. The result shows at once: what they used and what's left, or the reason they can't come in. On the web app now.",
    points: [
      "Camera, scanner or name search.",
      "A clear reason when a pass can't be used.",
      "Undo for 10 seconds, and every override logged with a reason.",
    ],
    heroImage: "/images/features/check-ins/check-ins-result-card.webp",
    heroImageAlt: "Check-in result on a phone: 1 class used, 7 left, Studio B Vinyasa flow is ready, for Nora Patel on 10 classes a month, 7 of 10 classes left, an Undo button with 10 seconds, and tabs for Camera, Scanner, Search and Today.",
    heroLabel: "Check-in",
    connects:
      "Check-ins connect to Memberships for each member's balance and rules, the client portal and Apple Wallet for the pass, and Reports for the Check-ins report.",
    workflowTitle: "Check a member in, in four steps.",
    workflow: [
      {
        title: "Open Check-in",
        copy: "Go to Clients, then Check-in. The screen says Ready for the next member.",
      },
      {
        title: "Scan or search",
        copy: "Scan the pass with Camera or Scanner, or use Search with a name or the 12-digit number on the card.",
      },
      {
        title: "Read the result",
        copy: "Checked in, with what was used and what's left, or the reason the member can't come in.",
      },
      {
        title: "Fix or override",
        copy: "Undo within 10 seconds, or choose Check in anyway with a reason, and a manager PIN where it's needed.",
      },
    ],
    capabilitiesTitle: "What Check-ins covers.",
    capabilitiesIntro:
      "Check-ins read each member's membership, so the desk sees the same balance and rules you set up.",
    capabilities: [
      {
        label: "Scan",
        title: "Three ways in",
        copy: "Camera, a USB or Bluetooth scanner, or search by name or card number.",
      },
      {
        label: "Result",
        title: "A clear answer",
        copy: "Checked in with what's used and left, or why not: Expired, Payment failed, Membership frozen, Already checked in, Too soon for another visit or Daily limit reached.",
      },
      {
        label: "Undo",
        title: "Fix it in 10 seconds",
        copy: "Undo a check-in for 10 seconds, and it doesn't count.",
      },
      {
        label: "Override",
        title: "Exceptions on the record",
        copy: "Check in anyway with a reason, and a manager PIN for the stricter cases; it's logged with the staff member's name.",
      },
      {
        label: "Sell",
        title: "Top up at the desk",
        copy: "Out of visits or past its date? Sell a new pack right from the check-in screen; the member pays through the usual Memberships pay link. Recurring plans show Renew instead.",
      },
      {
        label: "Report",
        title: "Busiest times and the desk log",
        copy: "Check-ins per day, busiest times by weekday and hour, and a desk log of overrides, for 7, 30 or 90 days.",
      },
    ],
    showcaseTitle: "What the desk sees.",
    showcaseIntro:
      "These are Check-in screens from the web app. The studio, members and numbers are sample data.",
    showcase: [
      {
        title: "When a pass can't be used",
        copy: "The result says why, and offers what to do next: sell a new pack, or allow the visit once.",
        image: "/images/features/check-ins/check-ins-expired-sell-a-pack.webp",
        imageAlt: "Check-in result Expired Oct 2 for Chris Wu on a 10 session pack, with Sell a pack and Allow once buttons.",
        caption: "An expired pack, with Sell a pack right there.",
        label: "Check-in",
      },
      {
        title: "Overrides on the record",
        copy: "Check in anyway asks for a reason, and a manager PIN for the stricter cases, and it's logged with your name.",
        image: "/images/features/check-ins/check-ins-manager-pin-override.webp",
        imageAlt: "Check Kim in anyway sheet: too soon for another visit, last visit 18 hours ago, logged with your name, reasons Forgot pass, Card fixed at desk, Owner OK'd it and Other, and a 4-digit manager PIN.",
        caption: "Every exception has a reason and a name.",
        label: "Check-in",
      },
      {
        title: "The Check-ins report",
        copy: "Check-ins per day, the busiest times by weekday and hour, and who overrode what.",
        image: "/images/features/check-ins/check-ins-report.webp",
        imageAlt: "Check-ins report for the last 30 days: a summary sentence, tiles for check-ins, turned away at the door, desk overrides and members now, a bar chart of check-ins per day, and a busiest-times heat map by weekday and hour.",
        caption: "Finance, then Reports, then Check-ins.",
        label: "Reports",
      },
    ],
    useCasesTitle: "Check-ins at your front desk.",
    useCases: [
      {
        title: "Studio front desk",
        copy: "Scan class-pack passes; when the last class is used, sell a new pack from the result.",
      },
      {
        title: "Gym, busy evenings",
        copy: "A scanner at the desk keeps the line moving, and the busiest-times map shows when to staff up.",
      },
      {
        title: "Coworking",
        copy: "Day passes checked in by name search when someone forgets their phone.",
      },
      {
        title: "Salon or spa",
        copy: "Monthly members checked in at reception; a paused membership shows the reason, Membership frozen.",
      },
    ],
    sidebarTitle: "Before you start",
    sidebarCopy: "Set up memberships, then open Clients, then Check-in.",
    ctaTitle: "Set up memberships and check in your first member.",
  },
  programs: {
    eyebrow: "Programs",
    title: "Sell coaching programs in tiers, and see who shows up",
    metaTitle: "Sell Coaching Programs in Tiers",
    metaDescription:
      "Sell coaching programs in tiers with a price, term and sessions each month. Enrol clients with a coach and track sessions and show-up rate.",
    intro:
      "Set each tier's price, term and sessions a month, enrol a client with their coach, and the program starts when they pay. Members, renewals due and the show-up rate sit on one page. On the web app now.",
    points: [
      "Tiers with a monthly price, a term and sessions per month.",
      "Enrol a client with a coach, paid through a Stripe pay link.",
      "Members, sessions and show-up rate on one program page.",
    ],
    heroImage: "/images/features/programs/programs-program-page.webp",
    heroImageAlt: "Program page for Website Growth Lab: tiles for members, monthly revenue, renewals due in November and left in October; Core at $297 a month and Inner Circle at $997 a month, each a 6-month term then month to month; and a member list with tier, coach, term, next session and status.",
    heroLabel: "Programs",
    connects:
      "Programs connect to Memberships for billing and the client's portal line, your calendar for booked sessions, client records for the program card, and Reports for the Programs & Sessions report.",
    workflowTitle: "Set up a coaching program in four steps.",
    workflow: [
      {
        title: "Create the program",
        copy: "Go to Finance, then Programs, then New program. New programs start from the Coaching program preset: a monthly price, a 6-month term and sessions with a coach.",
      },
      {
        title: "Add tiers",
        copy: "Give each tier a Price per month, a Term (months), Sessions per month, a one-line summary and any group calls. Set a tier to Hidden to stop selling it.",
      },
      {
        title: "Enroll a client",
        copy: "Choose the client, the tier and their coach. The membership starts when the client pays.",
      },
      {
        title: "Book and note sessions",
        copy: "Book the next session from the client's program card, and add a Session note afterwards. You book sessions; clients don't book their own.",
      },
    ],
    capabilitiesTitle: "What Programs covers.",
    capabilitiesIntro:
      "Programs run on Memberships, so tiers bill monthly through your own Stripe account. They are not instalment payment plans.",
    capabilities: [
      {
        label: "Tiers",
        title: "Price, term and sessions",
        copy: "Each tier has a monthly price, a term in months, sessions per month and optional group calls.",
      },
      {
        label: "Enrol",
        title: "Client, tier and coach",
        copy: "Enrol a client in a tier with their coach; they pay through a Stripe pay link to your account.",
      },
      {
        label: "Members",
        title: "Where everyone is",
        copy: "Tier, coach, month of the term, next session and status: Renewal due, Frozen, Payment overdue or Awaiting payment.",
      },
      {
        label: "Sessions",
        title: "Who shows up",
        copy: "Booked, attended and no-show sessions from your calendar, with the show-up rate.",
      },
      {
        label: "Client card",
        title: "On the client's record",
        copy: "Program, coach, term, sessions used and next charge, with Book session and Session note.",
      },
      {
        label: "Report",
        title: "By coach and by tier",
        copy: "Compare sessions and show-up rate by coach, and members, joins, leavers and renewals due by tier. The Programs & Sessions report is on Voyager and up.",
      },
    ],
    showcaseTitle: "What you and your coaches see.",
    showcaseIntro:
      "These are Programs screens from the web app. The program, clients, coaches, numbers and dates are sample data.",
    showcase: [
      {
        title: "Build the tiers",
        copy: "Each tier gets its own price per month, term and sessions, a one-line summary and its group calls.",
        image: "/images/features/programs/programs-tier-editor.webp",
        imageAlt: "Program editor: program name Website Growth Lab, and the Core tier set to $297 price per month, a 6-month term and 1 session per month, with a one-line summary, a Core group call, and Selling or Hidden.",
        caption: "The tier editor.",
        label: "Programs",
      },
      {
        title: "Who shows up",
        copy: "The Sessions tab lists each member's booked, attended and no-show sessions, with the show-up rate on top.",
        image: "/images/features/programs/programs-sessions-tab.webp",
        imageAlt: "Sessions tab on a phone: tiles for 6 sessions with 2 upcoming and 2 group calls, 3 attended, 1 no-show and a 75% show-up rate of 4 past sessions, then sessions for Noah Bennett, Chris Baker and a group call with Booked, Upcoming and Attended statuses.",
        caption: "The Sessions tab on a phone.",
        label: "Programs",
      },
      {
        title: "On the client's record",
        copy: "The program card shows the tier, coach, month of the term, sessions used and the next charge, with Book session and Session note.",
        image: "/images/features/programs/programs-client-card.webp",
        imageAlt: "Client program card: Website Growth Lab, Inner Circle, coach Ava Martinez, month 4 of 6 ending Jan 5, 16 of 24 sessions used with the next one on Thursday Oct 29 at 10:00 AM, $997 a month with the next charge Nov 6, and Book session and Session note buttons.",
        caption: "The program card on a client's page.",
        label: "Clients",
      },
      {
        title: "The Programs & Sessions report",
        copy: "Sessions, attendance and show-up rate by coach, and members, joins, leavers and renewals due by tier.",
        image: "/images/features/programs/programs-sessions-report.webp",
        imageAlt: "Programs and Sessions report for this month: 49 one-to-one sessions, 43 attended (90%) and 7 group calls held, and a By coach table with sessions, attended, no-shows and show-up rate for Ava Martinez, Nate Kim, a former team member and unassigned sessions.",
        caption: "Finance, then Reports, then Programs & Sessions.",
        label: "Reports",
      },
    ],
    useCasesTitle: "Programs for coaches.",
    useCases: [
      {
        title: "Business coach, two tiers",
        copy: "Core with a weekly group call and one one-to-one a month; Inner Circle with a one-to-one every week.",
      },
      {
        title: "Fitness coach, 6-month program",
        copy: "The Coaching program preset with sessions each month; the Sessions tab shows each member's booked, attended and no-show sessions.",
      },
      {
        title: "Coaching team",
        copy: "Two coaches on one program; the report compares sessions and show-up rate by coach.",
      },
      {
        title: "Hide a tier",
        copy: "Set a tier to Hidden to stop selling it.",
      },
    ],
    sidebarTitle: "Before you start",
    sidebarCopy: "Connect Stripe, then create a program.",
    ctaTitle: "Connect Stripe and create your first program.",
  },
  memberships: {
    eyebrow: "Memberships",
    title: "Sell memberships and class packs, and give every member a pass",
    metaTitle: "Membership Management Software for Studios",
    metaDescription:
      "Sell memberships, class packs and passes with a pay link. Recurring plans renew automatically, and every member gets a pass in their portal and Apple Wallet.",
    intro:
      "For studios, gyms, salons and coworking spaces. Start from a preset, send a pay link, and the membership starts when they pay. Recurring plans renew on their own. On the web app now.",
    points: [
      "Presets for Unlimited, Minutes, Visit pack and Credits.",
      "A pass for every member, in their client portal and Apple Wallet.",
      "Freeze, adjust a balance, refund or cancel from one member page.",
    ],
    heroImage: "/images/features/memberships/memberships-new-membership.webp",
    heroImageAlt: "Start from a preset on the New membership screen: the Credits preset picked from the membership presets, What's included set to 8 credits every month, and Billing with Recurring, One time and Term.",
    heroLabel: "Memberships",
    connects:
      "Memberships connect to your own Stripe account for payments, the client portal for each member's pass, online booking for Book next visit, and check-ins at the front desk.",
    workflowTitle: "Sell your first membership in four steps.",
    workflow: [
      {
        title: "Start from a preset",
        copy: "Go to Clients, then Memberships, choose New membership and Start from a preset.",
      },
      {
        title: "Set what's included and billing",
        copy: "Set What's included, then Billing: Recurring, One time or Term, with an optional sign-up fee and commitment. At the door holds freeze rules and guest passes.",
      },
      {
        title: "Sell it with a pay link",
        copy: "On a client, choose Sell membership, then Pay link. You get a link to send, and the membership starts when they pay.",
      },
      {
        title: "Send it your way",
        copy: "Text it, email it, show a QR code or copy the link. Nothing is sent until you do.",
      },
    ],
    capabilitiesTitle: "What Memberships covers.",
    capabilitiesIntro:
      "Everything here works in the web app today, with payments through your own Stripe account.",
    capabilities: [
      {
        label: "Presets",
        title: "Start from a preset",
        copy: "Unlimited, Minutes, Visit pack and Credits, plus Salon membership, Coworking and Day pass presets.",
      },
      {
        label: "Billing",
        title: "Plans, packs and terms",
        copy: "Recurring plans renew automatically; packs and one-time types don't. Add a sign-up fee or a minimum commitment.",
      },
      {
        label: "Selling",
        title: "A pay link you send",
        copy: "Text it, email it, show a QR code or copy the link. Nothing is sent until you do.",
      },
      {
        label: "Members",
        title: "One page per member",
        copy: "Balance, pass and history, with Freeze, Adjust balance, Refund and Cancel. Needs attention flags a balance to check.",
      },
      {
        label: "Pass",
        title: "A pass in their pocket",
        copy: "A QR pass in the client portal, with Add to Apple Wallet on iPhone. Send the pass yourself by text or email, or reissue it.",
      },
      {
        label: "Portal",
        title: "Members see their own plan",
        copy: "Their plan, balance, payments and recent visits, plus Book next visit when you have online booking set up.",
      },
    ],
    showcaseTitle: "What you and your members see.",
    showcaseIntro:
      "These are Memberships screens from the web app and the member's pass page. The business, plans and members are sample data.",
    showcase: [
      {
        title: "Sell membership",
        copy: "Before you send the link, the sheet spells out what is charged now and what happens next.",
        image: "/images/features/memberships/memberships-sell-class-pack.webp",
        imageAlt: "Sell membership for a 10-class pack at $79.00 that expires in 180 days: first charge $79.00, then nothing more because it doesn't renew, paid with a pay link.",
        caption: "Sell membership spells out what is charged and when.",
        label: "Memberships",
      },
      {
        title: "The member page",
        copy: "Balance, pass and history in one place, with Freeze, Adjust balance, Refund and Cancel membership.",
        image: "/images/features/memberships/memberships-member-page.webp",
        imageAlt: "A member page with Change plan, Freeze, Adjust balance, Refund and Cancel membership buttons, a balance of 7 of 10 credits, and the member's pass with a Show pass button.",
        caption: "Run each member from one page.",
        label: "Memberships",
      },
      {
        title: "The member's pass",
        copy: "Every member gets a pass with a QR code to show at the front desk, and Add to Apple Wallet on iPhone.",
        image: "/images/features/memberships/memberships-member-pass.webp",
        imageAlt: "Northside Yoga Studio pass for Maya: a QR code with its code underneath, Show this code at the front desk, and an Add to Apple Wallet button.",
        caption: "The pass lives in the client portal and Apple Wallet.",
        label: "Client portal",
      },
    ],
    useCasesTitle: "Memberships for the way you sell.",
    useCases: [
      {
        title: "Yoga studio, class credits",
        copy: "Credits preset with 10 credits a month, sold to each new student with a pay link.",
      },
      {
        title: "Gym, unlimited monthly",
        copy: "Unlimited preset, recurring monthly with a sign-up fee; the member page shows the balance, pass and history.",
      },
      {
        title: "Salon, monthly membership",
        copy: "Salon membership preset, recurring monthly; freeze it from the member page when a client travels.",
      },
      {
        title: "Coworking, day passes",
        copy: "Day pass preset, sold one time; the member shows their pass at the front desk.",
      },
    ],
    sidebarTitle: "Before you start",
    sidebarCopy: "Connect Stripe, then start from a preset.",
    ctaTitle: "Connect Stripe and sell your first membership.",
  },
  "time-tracking": {
    eyebrow: "Time Tracking",
    title: "Track the hour, then bill it in two clicks",
    metaTitle: "Time tracking with invoicing for client work",
    metaDescription:
      "Run a timer or log time by hand, see the week on a timesheet with optional approval, and bill unbilled hours onto an invoice at the project or member rate.",
    intro:
      "Start a timer on a project or task, or add the time afterwards. Each entry waits as Unbilled until you put it on an invoice.",
    points: [
      "Timers and manual entries, on the web, iPhone and iPad.",
      "A billable rate and a separate cost rate on every entry.",
      "Weekly timesheets, with approval when the team wants it.",
    ],
    connects:
      "Time entries connect to clients, projects, tasks, invoices, retainers and Home, and from Specialist to the job profitability report.",
    examplesTitle: "Time routines teams repeat.",
    examplesIntro:
      "These examples show how time moves through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Friday Billing Run",
        status: "Active",
        tag: "Billing",
        steps: [
          { tone: "trigger", text: "Open Time Tracking and filter Unbilled" },
          { tone: "step", text: "Choose Bill time for the client" },
          { tone: "condition", text: "The client has a draft invoice" },
          { tone: "step", text: "Add the entries as detailed lines" },
          { tone: "task", text: "Review the invoice and send it" },
        ],
      },
      {
        name: "Weekly Timesheet Review",
        status: "Active",
        tag: "Approvals",
        steps: [
          { tone: "trigger", text: "Turn on weekly timesheet approval" },
          { tone: "step", text: "Members submit their week" },
          { tone: "condition", text: "A week is waiting under Approvals" },
          { tone: "step", text: "Review it by day or by project" },
          { tone: "task", text: "Approve it, or reject it with a reason" },
        ],
      },
    ],
    sidebarTitle: "Bill the hours you worked.",
    sidebarCopy: "Start a free trial and run your first timer in Workspace369.",
  },
  "multi-currency-invoicing": {
    eyebrow: "Currency & Tax",
    title: "Invoices in your currency, with the right decimals and taxes",
    metaTitle: "Multi-currency invoicing software",
    metaDescription:
      "Bill in your own currency, from CAD to JPY and KWD, with tax codes built from components, card payments through Stripe and invoice pages in your language.",
    intro:
      "Pick your currency once and every invoice, proposal and retainer uses it. Yen and dinars are charged in their own units, and tax codes add up their parts.",
    points: [
      "Your workspace currency on every invoice, proposal and report.",
      "Yen in whole units and dinars to three decimals, paid by card through Stripe.",
      "Tax codes built from components, and invoice pages in your document language.",
    ],
    connects:
      "Currency connects to invoices, proposals, retainers, tax codes, card payments through your own Stripe account and the invoice page.",
    examplesTitle: "Currency setups teams repeat.",
    examplesIntro:
      "These examples show how businesses outside the US set up billing in Workspace369. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "Set Up a Canadian Workspace",
        status: "Active",
        tag: "Currency",
        steps: [
          { tone: "trigger", text: "Open Settings, then General" },
          { tone: "step", text: "Choose CAD · Canadian Dollar" },
          { tone: "step", text: "Add a GST + PST tax code with two components" },
          { tone: "condition", text: "Set it as the default tax code" },
          { tone: "task", text: "Create the next invoice in CAD" },
        ],
      },
      {
        name: "Invoice Pages in French",
        status: "Active",
        tag: "Language",
        steps: [
          { tone: "trigger", text: "Set the document language to French" },
          { tone: "step", text: "Open Document themes" },
          { tone: "step", text: "Page language: Document language (Français)" },
          { tone: "email", text: "Send the invoice link" },
          { tone: "task", text: "The client reads the labels in French" },
        ],
      },
    ],
    sidebarTitle: "Bill in your currency",
    sidebarCopy: "Invoices, card payments and retainers in your currency come with every plan.",
    heroLabel: "Currency and tax on an invoice",
    heroMode: "records",
    showcaseTitle: "See how currency and tax appear on an invoice.",
    ctaTitle: "Send your next invoice in your own currency.",
  },
  accounting: {
    eyebrow: "Accounting",
    title: "Books that fill in from the work you already do",
    metaTitle: "Accounting software for agencies and freelancers",
    metaDescription:
      "Double-entry accounting inside your CRM. Invoices, payments and expenses become journal entries automatically, on a cash or accrual basis. From Specialist.",
    intro:
      "The invoices, payments and expenses you already record become double-entry journal entries, on a cash or accrual basis. Your books stay next to the client work.",
    points: [
      "A chart of accounts from a template, cash or accrual.",
      "Invoices, payments and expenses post as journal entries.",
      "Reports export as PDF or CSV for your accountant.",
    ],
    connects:
      "Accounting connects to clients, invoices, payments, expenses, retainers, projects, vendors and bills, and the reports.",
    examplesTitle: "Accounting routines teams repeat.",
    examplesIntro:
      "These examples show how accounting moves through connected client work. They are product workflows, not promised business outcomes.",
    examples: [
      {
        name: "First Month on the Books",
        status: "Active",
        tag: "Setup",
        steps: [
          { tone: "trigger", text: "Run Accounting Setup: template, fiscal year, method" },
          { tone: "step", text: "Import opening balances from CSV" },
          { tone: "step", text: "Run Historical Data Migration up to a cutoff date" },
          { tone: "task", text: "Check the chart of accounts balances" },
        ],
      },
      {
        name: "Month-End Close",
        status: "Active",
        tag: "Commander",
        steps: [
          { tone: "trigger", text: "Approve entries in the review queue" },
          { tone: "step", text: "Run Profit & Loss against the previous month" },
          { tone: "condition", text: "Spending runs over budget" },
          { tone: "step", text: "Open Budget vs Actual for that account" },
          { tone: "task", text: "Export the reports as PDF for your accountant" },
        ],
      },
    ],
    sidebarTitle: "Books beside the work",
    sidebarCopy: "Accounting starts on Specialist. Set it up in four steps.",
  },
};

const featureCardInfo = {
  "client-portal": {
    name: "Client Portal",
    copy: "A private page per client to pay invoices, review proposals and sign contracts.",
  },
  schedule: {
    name: "Projects & Calendar",
    copy: "Projects, task boards and a Gantt view, a team calendar, and online booking.",
  },
  "online-booking": {
    name: "Online Booking",
    copy: "Booking pages with free times only, intake questions and deposits at booking.",
  },
  communications: {
    name: "Inbox & Communications",
    copy: "Shared inbox, email, SMS, calling, and voicemail in one workspace.",
  },
  invoices: {
    name: "Billing & Invoices",
    copy: "Turn an accepted proposal into an invoice without retyping client details.",
  },
  payments: {
    name: "Payments",
    copy: "Payment links, deposits, and payment plans connected to every invoice.",
  },
  expenses: {
    name: "Expense Management",
    copy: "Scan receipts with AI, link costs to projects and post them to your books.",
  },
  autopilot: {
    name: "AI AutoPilot Today",
    copy: "A ranked list of what needs you, Client Health and watches on money, projects and tasks.",
  },
  "ai-chat": {
    name: "Workspace AI",
    copy: "Chat, drafting, translation, and knowledge search inside the workspace.",
  },
  automation: {
    name: "Workflow Automations",
    copy: "Email, SMS, tasks, reminders, and team notifications with test runs and logs.",
  },
  analytics: {
    name: "Dashboards & Reports",
    copy: "AR aging, profitability, and activity reporting across client work.",
  },
  files: {
    name: "Files & Storage",
    copy: "Files, notes, and shared records attached to the work they belong to.",
  },
  products: {
    name: "Products & Services",
    copy: "Catalog items, packages, and inventory for cleaner billing.",
  },
  support: {
    name: "Support & Scale",
    copy: "Onboarding, dedicated support, and plan options as the team grows.",
  },
  themes: {
    name: "Themes & Dark Mode",
    copy: "Light, dark or system, accent colors, and 35 themes for client pages.",
  },
  branding: {
    name: "Branding",
    copy: "Your logo and colors on the portal, booking page, emails and invoices.",
  },
  "pdf-customization": {
    name: "Document Design",
    copy: "Invoice PDF templates, invoice page layouts and proposal themes.",
  },
  quoting: {
    name: "Proposals",
    copy: "Clients accept, sign and pay the deposit from one proposal link.",
  },
  "workspace-voice": {
    name: "Workspace Voice",
    copy: "Business calling with routing, recording, and AI call summaries.",
  },
  "auto-attendant-and-ivr": {
    name: "Auto-Attendant & IVR",
    copy: "Menus and routing that send every caller to the right place.",
  },
  "post-call-automation": {
    name: "Post-Call Automation",
    copy: "Turn call outcomes into follow-up workflows automatically.",
  },
  contracts: {
    name: "Contracts & E-signature",
    copy: "Send contracts for e-signature with an audit trail and certificate of completion.",
  },
  "retainers-and-recurring-billing": {
    name: "Retainers & Recurring Billing",
    copy: "Hour banks, fixed monthly and prepaid retainers, recurring invoices and card autopay with consent.",
  },
  "change-orders": {
    name: "Change Orders",
    copy: "Versioned scope changes with the money impact and client approval by link.",
  },
  "flash-pay": {
    name: "Flash Pay",
    copy: "Take a payment on the spot with a QR code or a pay link; each one becomes a paid invoice.",
  },
  "mobile-app": {
    name: "iPhone & iPad App",
    copy: "The same workspace as the web app, on iPhone and iPad.",
  },
  "check-ins": {
    name: "Check-ins",
    copy: "Check members in by camera, scanner or name search, with a clear reason when a pass can't be used.",
  },
  programs: {
    name: "Programs",
    copy: "Sell coaching programs in tiers with a price, term and sessions, and see who shows up.",
  },
  memberships: {
    name: "Memberships",
    copy: "Sell memberships and class packs with a pay link, and give every member a pass.",
  },
  "time-tracking": {
    name: "Time Tracking",
    copy: "Timers, weekly timesheets, billable rates, and time billed onto invoices.",
  },
  "multi-currency-invoicing": {
    name: "Multi-Currency Invoicing",
    copy: "Bill in your currency, with tax codes, Stripe payments and invoice pages in your language.",
  },
  accounting: {
    name: "Accounting",
    copy: "Double-entry accounting inside your CRM, with cash or accrual books and reports.",
  },
};

const relatedFeatureMap = {
  "client-portal": ["contracts", "communications", "invoices", "mobile-app"],
  schedule: ["online-booking", "client-portal", "automation", "invoices"],
  "online-booking": ["schedule", "payments", "invoices", "automation"],
  communications: ["client-portal", "ai-chat", "automation", "workspace-voice"],
  invoices: ["payments", "retainers-and-recurring-billing", "multi-currency-invoicing", "accounting"],
  payments: ["invoices", "flash-pay", "retainers-and-recurring-billing", "accounting"],
  "flash-pay": ["payments", "invoices", "mobile-app", "online-booking"],
  expenses: ["invoices", "accounting", "mobile-app", "time-tracking"],
  "ai-chat": ["autopilot", "communications", "automation", "files"],
  analytics: ["autopilot", "expenses", "invoices", "payments"],
  autopilot: ["ai-chat", "analytics", "invoices", "automation"],
  files: ["client-portal", "schedule", "contracts", "invoices"],
  products: ["quoting", "invoices", "expenses", "analytics"],
  support: ["ai-chat", "client-portal", "themes", "files"],
  themes: ["branding", "pdf-customization", "client-portal", "support"],
  branding: ["themes", "pdf-customization", "client-portal", "quoting"],
  "pdf-customization": ["branding", "quoting", "invoices", "themes"],
  quoting: ["contracts", "change-orders", "invoices", "payments"],
  "auto-attendant-and-ivr": ["workspace-voice", "post-call-automation", "communications", "ai-chat"],
  contracts: ["quoting", "change-orders", "invoices", "client-portal"],
  "retainers-and-recurring-billing": ["invoices", "payments", "time-tracking", "multi-currency-invoicing"],
  "change-orders": ["contracts", "quoting", "invoices", "client-portal"],
  "mobile-app": ["time-tracking", "invoices", "expenses", "communications"],
  "time-tracking": ["retainers-and-recurring-billing", "invoices", "mobile-app", "analytics"],
  memberships: ["check-ins", "client-portal", "payments", "online-booking"],
  "check-ins": ["memberships", "client-portal", "analytics", "online-booking"],
  programs: ["memberships", "client-portal", "analytics", "online-booking"],
  "multi-currency-invoicing": ["invoices", "payments", "retainers-and-recurring-billing", "quoting"],
  accounting: ["invoices", "expenses", "payments", "analytics"],
};

const story = computed(() => storyData || null);
const keeps = (section) => !story.value || Boolean(story.value.keep?.includes(section));

const verifiedHeroImages = {
  "client-portal": "/home/screens/crm-light-clean.webp",
  schedule: "/home/screens/calendar-month-light-clean.webp",
  communications: "/home/screens/inbox-light-clean.webp",
  invoices: "/home/screens/invoice-list-light-clean.webp",
  payments: "/images/release-notes/2026-10-17-more-ways-for-clients-to-pay/payment-methods.webp",
  analytics: "/home/screens/dashboard-light-clean.webp",
  themes: "/images/feature-themes.jpg",
  "pdf-customization": "/images/pdf-customization.jpg",
  quoting: "/images/release-notes/2026-10-04-proposal-builder/proposal-editor.webp",
  "retainers-and-recurring-billing": "/home/screens/invoice-list-light-clean.webp",
  "time-tracking": "/home/screens/dashboard-light-clean.webp",
  "mobile-app": "/images/feature-mobile-app.webp",
};

const previewModes = {
  "client-portal": ["records", "timeline", "workflow"],
  schedule: ["timeline", "records", "workflow"],
  "online-booking": ["workflow", "records", "timeline"],
  communications: ["records", "timeline", "workflow"],
  invoices: ["records", "workflow", "timeline"],
  payments: ["workflow", "records", "timeline"],
  "flash-pay": ["workflow", "records", "timeline"],
  expenses: ["records", "timeline", "workflow"],
  "ai-chat": ["records", "workflow", "timeline"],
  analytics: ["records", "timeline", "workflow"],
  files: ["records", "timeline", "workflow"],
  products: ["records", "workflow", "timeline"],
  support: ["records", "timeline", "workflow"],
  themes: ["records", "workflow", "timeline"],
  branding: ["records", "workflow", "timeline"],
  "pdf-customization": ["records", "workflow", "timeline"],
  quoting: ["workflow", "records", "timeline"],
  contracts: ["workflow", "timeline", "records"],
  "retainers-and-recurring-billing": ["records", "workflow", "timeline"],
  "change-orders": ["timeline", "records", "workflow"],
  "mobile-app": ["records", "timeline", "workflow"],
  "time-tracking": ["records", "workflow", "timeline"],
  memberships: ["records", "workflow", "timeline"],
  "check-ins": ["records", "workflow", "timeline"],
  programs: ["records", "workflow", "timeline"],
  "multi-currency-invoicing": ["records", "workflow", "timeline"],
  accounting: ["records", "workflow", "timeline"],
};

const defaultWorkflowTitles = [
  "Start with the right record",
  "Move the work forward",
  "Keep the result visible",
];

const feature = computed(() => {
  const source = featureData[props.featureKey] || featureData["client-portal"];
  const enhancement = seoEnhancement;
  const base = {
    ...source,
    ...enhancement,
    faq: enhancement.faq || source.faq,
    relatedLinks: enhancement.relatedLinks || source.relatedLinks,
  };
  const modes = previewModes[props.featureKey] || ["records", "workflow", "timeline"];
  const workflow = base.workflow || base.points.map((point, index) => ({
    title: defaultWorkflowTitles[index] || point,
    copy: point,
  }));
  const showcase = base.showcase || base.points.map((point, index) => ({
    eyebrow: `Product view ${String(index + 1).padStart(2, "0")}`,
    title: point,
    copy: [base.intro, base.connects, base.sidebarCopy][index] || base.connects,
    mode: modes[index] || "records",
    label: base.sidebarTitle,
  }));

  return {
    ...base,
    heroImage: base.heroImage ?? verifiedHeroImages[props.featureKey] ?? "",
    heroImageAlt: base.heroImageAlt || `${base.title} in Workspace369`,
    heroLabel: base.heroLabel || `${base.eyebrow} product view`,
    heroMode: base.heroMode || modes[0] || "records",
    workflow,
    workflowTitle: base.workflowTitle || "Move from first signal to completed work with context intact.",
    showcase,
    showcaseTitle: base.showcaseTitle || "See how the feature fits into day-to-day work.",
    showcaseIntro:
      base.showcaseIntro ||
      "Follow the record through the views teams use to act, review progress, and keep the next step clear.",
    faqTitle: base.faqTitle || `${base.eyebrow} questions`,
    ctaTitle: base.ctaTitle || "Bring this workflow into the rest of Workspace369.",
    ctaCopy:
      base.ctaCopy || "Start free and bring this workflow into the rest of your workspace.",
    capabilities: base.capabilities,
    capabilitiesEyebrow: base.capabilitiesEyebrow || "What it covers",
    capabilitiesTitle: base.capabilitiesTitle || "What the feature covers.",
    capabilitiesIntro: base.capabilitiesIntro || "",
    examples: base.examples,
    examplesEyebrow: base.examplesEyebrow || "Examples",
    examplesTitle: base.examplesTitle || "See it in practice.",
    examplesIntro: base.examplesIntro || "",
    relatedFeatures: (relatedFeatureMap[props.featureKey] || [])
      .map((key) => ({ ...featureCardInfo[key], path: `/features/${key}` }))
      .filter((item) => item.name),
    relatedFeaturesIntro:
      base.relatedFeaturesIntro ||
      "These features connect directly with this workflow in day-to-day client work.",
  };
});
// Story pages: the curated related links (feature-seo-content.js), less the features the related list shows.
const storyReadNext = computed(() => {
  if (!story.value) return [];
  const shown = new Set(feature.value.relatedFeatures.slice(0, 3).map((item) => item.path));
  return (feature.value.relatedLinks || []).filter((link) => !shown.has(link.path.replace(/\/$/, "")));
});
const pageTitle = computed(() => feature.value.metaTitle || feature.value.title);
const seoTitle = computed(() =>
  pageTitle.value.includes("Workspace369")
    ? pageTitle.value
    : `${pageTitle.value} | Workspace369`,
);
const pageDescription = computed(() => feature.value.metaDescription || feature.value.intro);
const siteUrl = "https://workspace369.com";
const { canonicalLocaleCode } = useWorkspaceLocale();
const canonicalUrl = computed(() =>
  localizedUrl(siteUrl, `/features/${props.featureKey}`, canonicalLocaleCode.value),
);

useHead(() => {
  const schemaGraph = [
    {
      "@type": "Service",
      "@id": `${canonicalUrl.value}#software-feature`,
      name: `Workspace369 ${feature.value.eyebrow}`,
      serviceType: `${feature.value.eyebrow} software feature`,
      url: canonicalUrl.value,
      description: pageDescription.value,
      provider: { "@id": `${siteUrl}/#organization` },
      audience: {
        "@type": "BusinessAudience",
        audienceType: "service businesses, agencies, consultants, and client-work teams",
      },
    },
    {
      "@type": "BreadcrumbList",
      "@id": `${canonicalUrl.value}#breadcrumbs`,
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${siteUrl}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Features",
          item: localizedUrl(siteUrl, "/features", currentLocale.value),
        },
        {
          "@type": "ListItem",
          position: 3,
          name: feature.value.eyebrow,
          item: canonicalUrl.value,
        },
      ],
    },
  ];

  if (feature.value.faq?.length) {
    schemaGraph.push({
      "@type": "FAQPage",
      "@id": `${canonicalUrl.value}#faq`,
      mainEntity: feature.value.faq.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    });
  }

  return {
    title: seoTitle.value,
    meta: [
      { name: "description", content: pageDescription.value },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: seoTitle.value },
      { name: "twitter:description", content: pageDescription.value },
      { property: "og:title", content: seoTitle.value },
      { property: "og:description", content: pageDescription.value },
      { property: "og:url", content: canonicalUrl.value },
    ],
    link: [{ key: "canonical", rel: "canonical", href: canonicalUrl.value }],
    script: [
      {
        key: `feature-schema-${props.featureKey}`,
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": schemaGraph,
        }),
      },
    ],
  };
});
</script>

<style scoped>
.feature-detail-page {
  background: #ffffff;
  color: #0f172a;
  letter-spacing: 0;
}

.feature-detail-hero {
  overflow: hidden;
  background:
    linear-gradient(90deg, rgb(15 143 177 / 0.04) 1px, transparent 1px),
    linear-gradient(#ffffff 0%, #f7fbfd 100%);
  background-size: 4rem 100%, 100% 100%;
  padding: 8.5rem 0 5.5rem;
}

.feature-shell {
  width: min(100% - 3rem, 84rem);
  margin: 0 auto;
}

.feature-breadcrumb {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 2.15rem;
  color: #7a8a9e;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
}

.feature-breadcrumb a {
  color: #0f7792;
  text-decoration: none;
}

.feature-breadcrumb a:hover,
.feature-breadcrumb a:focus-visible {
  color: #075e7a;
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}

.feature-hero-grid {
  display: grid;
  gap: 3rem;
  align-items: center;
}

.feature-section-heading:not(.feature-section-heading--split) > p {
  color: #0e7490;
  font-size: 0.74rem;
  font-weight: 900;
  line-height: 1.2;
  text-transform: uppercase;
}

.feature-hero-copy h1 {
  max-width: 48rem;
  margin-top: 0.8rem;
  color: #020617;
  font-size: clamp(2.6rem, 5.4vw, 4.35rem);
  font-weight: 880;
  letter-spacing: 0;
  line-height: 1.02;
  overflow-wrap: break-word;
  text-wrap: balance;
}

.feature-intro {
  max-width: 42rem;
  margin-top: 1.35rem;
  color: #475569;
  font-size: clamp(1.02rem, 1.8vw, 1.18rem);
  line-height: 1.68;
}

.feature-hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.7rem;
}

/* max-width none: with its default 34rem cap the line can fit beside the buttons on wide screens instead of under them. */
.feature-hero-actions :deep(.trial-disclosure) {
  flex: 0 0 100%;
  max-width: none;
  margin: 0;
}

/* 641px: up to 640px the row is a one-column grid (below), where DOM order already puts the terms under the trial button. */
@media (min-width: 641px) {
  .feature-hero-actions :deep(.trial-disclosure) {
    order: 2;
    /* The line stays a full-width row (so it never sits beside the buttons) but its text keeps the 34rem measure it had
       before: running full width it painted on one line, then wrapped to two once the font loaded (CLS ~0.011 at 1024). */
    padding-inline-end: max(0px, calc(100% - 34rem));
  }
}

/* Story heroes are centred from 761px: split the spare width so the 34rem text block stays centred. */
@media (min-width: 761px) {
  .is-story .feature-hero-actions :deep(.trial-disclosure) {
    padding-inline: max(0px, calc((100% - 34rem) / 2));
  }
}

.feature-primary,
.feature-secondary {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  padding: 0.75rem 1.2rem;
  font-size: 0.88rem;
  font-weight: 900;
  line-height: 1;
  text-decoration: none;
  transition: background-color 150ms ease, border-color 150ms ease, color 150ms ease,
    transform 150ms ease;
}

.feature-primary {
  border: 1px solid #0e7490;
  background: #0e7490;
  color: #ffffff;
}

.feature-secondary {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
}

.feature-primary:hover,
.feature-primary:focus-visible {
  border-color: #155e75;
  background: #155e75;
  transform: translateY(-1px);
}

.feature-secondary:hover,
.feature-secondary:focus-visible {
  border-color: #94a3b8;
  background: #f8fafc;
  transform: translateY(-1px);
}

.feature-hero-facts {
  display: grid;
  margin-top: 2.2rem;
  border-bottom: 1px solid #dbe4ee;
}

.feature-hero-facts > div {
  display: grid;
  grid-template-columns: 2.2rem minmax(0, 1fr);
  gap: 0.65rem;
  border-top: 1px solid #dbe4ee;
  padding: 0.8rem 0;
}

.feature-hero-facts dt {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
  line-height: 1.55;
}

.feature-hero-facts dd {
  color: #334155;
  font-size: 0.86rem;
  font-weight: 750;
  line-height: 1.55;
}

.feature-hero-frame {
  min-width: 0;
}

.feature-flow-section,
.feature-definition-section,
.feature-showcase-section,
.feature-use-cases-section,
.feature-answer-section,
.feature-faq-section {
  padding: clamp(4.5rem, 8vw, 7rem) 0;
}

.feature-flow-section {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.feature-definition-section {
  border-top: 1px solid #dbe4ee;
  background: #eef7fa;
}

.feature-definition-grid {
  display: grid;
  gap: 2.5rem;
  align-items: start;
}

.feature-definition-grid h2 {
  max-width: 34rem;
  margin-top: 0.55rem;
  color: #020617;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 880;
  line-height: 1.04;
  text-wrap: balance;
}

.feature-motion {
  border: 1px solid #d3e2ea;
}

.feature-showcase-steps {
  display: grid;
}

.feature-showcase-list--motion {
  gap: 2.5rem;
}

.feature-showcase-list--motion .feature-showcase-row {
  grid-template-columns: minmax(0, 1fr);
  padding: clamp(2.25rem, 4vw, 3.25rem) 0;
}

.feature-showcase-list--motion .feature-showcase-row:first-child {
  border-top: 0;
  padding-top: 0;
}

.feature-showcase-list--motion .feature-showcase-copy {
  order: 0;
}

@media (min-width: 1024px) {
  .feature-showcase-list--motion {
    grid-template-columns: minmax(0, 1fr) minmax(0, 32rem);
    align-items: start;
    gap: clamp(3rem, 6vw, 5.5rem);
  }

  .feature-showcase-list--motion > .feature-motion {
    position: sticky;
    top: 8rem;
    grid-column: 2;
    grid-row: 1;
  }

  .feature-showcase-list--motion > .feature-showcase-steps {
    grid-column: 1;
    grid-row: 1;
  }
}

.dark .feature-motion {
  border-color: rgb(255 255 255 / 0.1);
}

.feature-definition-copy {
  max-width: 50rem;
  color: #334155;
  font-size: 1.08rem;
  line-height: 1.76;
}

.feature-reference {
  display: grid;
  gap: 0.45rem;
  margin-top: 1.65rem;
  border-top: 1px solid #bfd5df;
  padding-top: 1rem;
}

.feature-reference > span {
  color: #0f7792;
  font-size: 0.7rem;
  font-weight: 900;
  text-transform: uppercase;
}

.feature-reference p {
  color: #475569;
  font-size: 0.78rem;
  line-height: 1.55;
}

.feature-reference a {
  display: inline-flex;
  width: fit-content;
  gap: 0.35rem;
  align-items: center;
  color: #0e7490;
  font-size: 0.8rem;
  font-weight: 850;
  text-decoration: none;
}

.feature-reference a:hover,
.feature-reference a:focus-visible {
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}

.feature-section-heading {
  max-width: 48rem;
}

.feature-section-heading--split {
  display: grid;
  max-width: none;
  gap: 2.5rem;
  align-items: end;
}

.feature-section-heading h2,
.feature-connected-grid h2,
.feature-answer-grid h2,
.feature-faq-grid h2,
.feature-final-cta h2 {
  margin-top: 0.55rem;
  color: #020617;
  font-size: clamp(2rem, 4vw, 3.25rem);
  font-weight: 880;
  line-height: 1.04;
  text-wrap: balance;
}

.feature-section-heading > span,
.feature-section-heading--split > p,
.feature-showcase-copy > p,
.feature-connected-grid > div:last-child > p,
.feature-answer-grid > div > p,
.feature-final-cta > div > div:first-child > p:last-child {
  color: #475569;
  font-size: 1rem;
  line-height: 1.7;
}

.feature-section-heading > span {
  display: block;
  max-width: 41rem;
  margin-top: 1rem;
}

.feature-flow-list {
  display: grid;
  gap: 1.4rem;
  margin-top: 3.25rem;
}

.feature-flow-list li {
  border-top: 2px solid #d4dee9;
  padding-top: 1rem;
}

.feature-flow-list li > span {
  color: #0e7490;
  font-size: 0.72rem;
  font-weight: 900;
}

.feature-flow-list h3 {
  margin-top: 1.15rem;
  color: #0f172a;
  font-size: 1.22rem;
  font-weight: 900;
  line-height: 1.22;
}

.feature-flow-list p {
  margin-top: 0.65rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.68;
}

.feature-showcase-section {
  border-top: 1px solid #e2e8f0;
  background: #f5f7fa;
}

.feature-use-cases-section {
  border-top: 1px solid #dbe4ee;
  background: #ffffff;
  padding: clamp(4.5rem, 8vw, 7rem) 0;
}

.feature-use-cases-grid {
  display: grid;
  gap: 1.1rem;
  margin-top: 3.25rem;
}

.feature-use-cases-grid article {
  border-top: 2px solid #1ba7ce;
  background: #f8fafc;
  padding: 1.4rem 1.25rem 1.5rem;
}

.feature-use-cases-grid article > span {
  color: #0e7490;
  font-size: 0.7rem;
  font-weight: 900;
}

.feature-use-cases-grid h3 {
  margin-top: 1.2rem;
  color: #0f172a;
  font-size: 1.18rem;
  font-weight: 900;
  line-height: 1.25;
}

.feature-use-cases-grid p {
  margin-top: 0.7rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.68;
}

.feature-showcase-list {
  display: grid;
  margin-top: clamp(3.5rem, 7vw, 6rem);
}

.feature-showcase-row {
  display: grid;
  gap: clamp(2rem, 5vw, 4.5rem);
  align-items: center;
  border-top: 1px solid #cfd9e4;
  padding: clamp(3rem, 7vw, 6rem) 0;
}

.feature-showcase-row:last-child {
  padding-bottom: 0;
}

.feature-showcase-copy h3 {
  margin-top: 0.65rem;
  color: #020617;
  font-size: clamp(1.65rem, 3vw, 2.45rem);
  font-weight: 900;
  line-height: 1.08;
  text-wrap: balance;
}

.feature-showcase-copy > p {
  margin-top: 1rem;
}

.feature-showcase-copy ul {
  display: grid;
  gap: 0.7rem;
  margin-top: 1.4rem;
}

.feature-showcase-copy li {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.7rem;
  align-items: start;
  color: #334155;
  font-size: 0.86rem;
  font-weight: 750;
  line-height: 1.55;
}

.feature-showcase-copy li::before {
  width: 0.42rem;
  height: 0.42rem;
  margin-top: 0.45rem;
  border-radius: 999px;
  background: #1ba7ce;
  content: "";
}

.feature-connected-section {
  background: #07101f;
  padding: clamp(4.5rem, 8vw, 7rem) 0;
}

.feature-connected-grid {
  display: grid;
  gap: 2.5rem;
  align-items: start;
}

.feature-connected-grid h2 {
  max-width: 31rem;
  color: #ffffff;
}

.feature-connected-grid > div:last-child > p {
  color: #c3cedd;
}

.feature-related-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.4rem;
  margin-top: 1.35rem;
}

.feature-related-links a,
.feature-answer-grid a {
  display: inline-flex;
  gap: 0.35rem;
  align-items: center;
  color: #67e8f9;
  font-size: 0.82rem;
  font-weight: 850;
  text-decoration: none;
}

.feature-related-links a:hover,
.feature-related-links a:focus-visible,
.feature-answer-grid a:hover,
.feature-answer-grid a:focus-visible {
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}

.feature-answer-section {
  background: #eef7fa;
}

.feature-answer-grid,
.feature-faq-grid {
  display: grid;
  gap: 2.5rem;
  align-items: start;
}

.feature-answer-grid h2 {
  margin-top: 0;
}

.feature-answer-grid > div > p {
  max-width: 50rem;
  margin-top: 1rem;
}

.feature-answer-grid a {
  margin-top: 1.2rem;
  color: #0e7490;
}

.feature-faq-section {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
}

.feature-faq-grid header {
  max-width: 28rem;
}

.feature-faq-grid details {
  border-top: 1px solid #dbe4ee;
  padding: 1.1rem 0;
}

.feature-faq-grid details:last-child {
  border-bottom: 1px solid #dbe4ee;
}

.feature-faq-grid summary {
  cursor: pointer;
  color: #0f172a;
  font-size: 1rem;
  font-weight: 850;
  line-height: 1.45;
}

.feature-faq-grid details p {
  max-width: 46rem;
  margin-top: 0.7rem;
  color: #475569;
  font-size: 0.9rem;
  line-height: 1.7;
}

.feature-final-cta > div > div:first-child > p:last-child {
  max-width: 42rem;
  margin-top: 1rem;
}

.dark .feature-detail-page {
  background: #020617;
  color: #e2e8f0;
}

.dark .feature-detail-hero {
  background:
    linear-gradient(90deg, rgb(103 232 249 / 0.04) 1px, transparent 1px),
    linear-gradient(180deg, #020617 0%, #07101f 100%);
  background-size: 4rem 100%, 100% 100%;
}

.dark .feature-breadcrumb {
  color: #8493a8;
}

.dark .feature-breadcrumb a {
  color: #67e8f9;
}

.dark .feature-hero-copy h1,
.dark .feature-definition-grid h2,
.dark .feature-section-heading h2,
.dark .feature-flow-list h3,
.dark .feature-showcase-copy h3,
.dark .feature-use-cases-grid h3,
.dark .feature-answer-grid h2,
.dark .feature-faq-grid h2,
.dark .feature-faq-grid summary,
.dark .feature-final-cta h2 {
  color: #ffffff;
}

.dark .feature-intro,
.dark .feature-definition-copy,
.dark .feature-hero-facts dd,
.dark .feature-section-heading > span,
.dark .feature-section-heading--split > p,
.dark .feature-flow-list p,
.dark .feature-showcase-copy > p,
.dark .feature-showcase-copy li,
.dark .feature-use-cases-grid p,
.dark .feature-answer-grid > div > p,
.dark .feature-faq-grid details p,
.dark .feature-final-cta > div > div:first-child > p:last-child {
  color: #cbd5e1;
}

.dark .feature-secondary {
  border-color: #304158;
  background: #0f172a;
  color: #f8fafc;
}

.dark .feature-hero-facts,
.dark .feature-hero-facts > div,
.dark .feature-definition-section,
.dark .feature-flow-section,
.dark .feature-showcase-section,
.dark .feature-showcase-row,
.dark .feature-use-cases-section,
.dark .feature-faq-section,
.dark .feature-faq-grid details {
  border-color: #243247;
}

.dark .feature-final-cta {
  border-color: rgb(255 255 255 / 0.1);
  background: #0a1a30;
}

.dark .feature-flow-section,
.dark .feature-use-cases-section,
.dark .feature-faq-section {
  background: #07101f;
}

.dark .feature-definition-section {
  background: #0d1b2d;
}

.dark .feature-reference {
  border-color: #304158;
}

.dark .feature-reference p {
  color: #94a3b8;
}

.dark .feature-reference a {
  color: #67e8f9;
}

.dark .feature-use-cases-grid article {
  background: #0a1220;
}

.dark .feature-showcase-section {
  background: #0a1220;
}

.dark .feature-answer-section {
  background: #0d1b2d;
}

.dark .feature-flow-list li {
  border-color: #304158;
}

.dark .feature-answer-grid a {
  color: #67e8f9;
}

@media (min-width: 760px) {
  .feature-flow-list,
  .feature-use-cases-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (min-width: 900px) {
  .feature-hero-grid {
    grid-template-columns: minmax(0, 0.9fr) minmax(24rem, 0.85fr);
    gap: clamp(3rem, 6vw, 6rem);
  }

  .feature-section-heading--split,
  .feature-definition-grid,
  .feature-connected-grid,
  .feature-answer-grid,
  .feature-faq-grid {
    grid-template-columns: minmax(0, 0.88fr) minmax(0, 1fr);
  }

  .feature-showcase-row {
    grid-template-columns: minmax(18rem, 0.68fr) minmax(0, 1.32fr);
  }

  .feature-showcase-row:nth-child(even) .feature-showcase-copy {
    order: 2;
  }

  .feature-showcase-row:nth-child(even) :deep(.product-frame) {
    order: 1;
  }

  .feature-showcase-copy {
    padding-inline: 0.3rem;
  }
}

@media (min-width: 1180px) {
  .feature-hero-frame {
    transform: translateX(1.5rem);
  }
}

@media (max-width: 899px) {
  .feature-detail-hero {
    padding-top: 7.5rem;
  }

  .feature-hero-grid {
    gap: 2.5rem;
  }

  .feature-showcase-row {
    gap: 2rem;
  }
}

@media (max-width: 640px) {
  .feature-detail-hero {
    padding: 6.75rem 0 3.75rem;
  }

  .feature-shell {
    width: min(100% - 2rem, 72rem);
  }

  .feature-breadcrumb {
    margin: 0 0 1.5rem;
    font-size: 0.68rem;
  }

  .feature-hero-copy {
    padding: 0 0.5rem;
  }

  .feature-hero-copy h1 {
    font-size: clamp(2.15rem, 11vw, 3.2rem);
    line-height: 1.02;
  }

  .feature-intro {
    font-size: 1rem;
    line-height: 1.62;
  }

  .feature-hero-actions {
    display: grid;
    grid-template-columns: 1fr;
  }

  .feature-primary,
  .feature-secondary {
    width: 100%;
  }

  .feature-flow-section,
  .feature-definition-section,
  .feature-showcase-section,
  .feature-use-cases-section,
  .feature-answer-section,
  .feature-faq-section,
  .feature-final-cta,
  .feature-connected-section {
    padding: 4rem 0;
  }

  .feature-section-heading,
  .feature-definition-grid,
  .feature-flow-list,
  .feature-use-cases-grid,
  .feature-connected-grid,
  .feature-answer-grid,
  .feature-faq-grid,
  .feature-final-cta__inner {
    padding-inline: 0.5rem;
  }

  .feature-section-heading h2,
  .feature-definition-grid h2,
  .feature-connected-grid h2,
  .feature-answer-grid h2,
  .feature-faq-grid h2,
  .feature-final-cta h2 {
    font-size: 2rem;
  }

  .feature-showcase-row {
    padding: 3.5rem 0;
  }

  .feature-showcase-copy {
    padding-inline: 0.5rem;
  }

  .feature-showcase-copy h3 {
    font-size: 1.65rem;
  }

  .feature-final-cta__inner .feature-hero-actions {
    width: 100%;
  }
}

/* ---------- Template v2 sections ---------- */

/* Capabilities */
.feature-capabilities-section {
  border-top: 1px solid #e2e8f0;
  background: #f5f7fa;
  padding: clamp(4.5rem, 8vw, 7rem) 0;
}

.feature-capabilities-grid {
  display: grid;
  gap: 1rem;
  margin-top: 3rem;
}

.feature-capabilities-grid article {
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  background: #ffffff;
  padding: 1.4rem 1.3rem 1.5rem;
  transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.feature-capabilities-grid article:hover {
  border-color: rgb(27 167 206 / 0.45);
  box-shadow: 0 16px 40px rgb(15 23 42 / 0.08);
  transform: translateY(-2px);
}

.feature-capabilities-grid article > span {
  display: inline-flex;
  width: fit-content;
  border-radius: 999px;
  background: #e0f2f7;
  padding: 0.26rem 0.68rem;
  color: #0b6e88;
  font-size: 0.64rem;
  font-weight: 900;
  text-transform: uppercase;
}

.feature-capabilities-grid h3 {
  margin-top: 1.05rem;
  color: #0f172a;
  font-size: 1.12rem;
  font-weight: 900;
  line-height: 1.25;
}

.feature-capabilities-grid p {
  margin-top: 0.6rem;
  color: #475569;
  font-size: 0.86rem;
  line-height: 1.64;
}

/* Mid-page CTA band */
.feature-mid-cta {
  background: linear-gradient(90deg, #0e7490 0%, #0f8fb1 60%, #14a0c7 100%);
}

.feature-mid-cta__inner {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 2.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 1.9rem 0;
}

.feature-mid-cta__inner > p {
  max-width: 44rem;
  color: #eafcff;
  font-size: clamp(1.05rem, 2vw, 1.3rem);
  font-weight: 850;
  line-height: 1.4;
}

.feature-primary--light {
  border-color: #ffffff;
  background: #ffffff;
  color: #0b6e88;
  box-shadow: 0 12px 26px rgb(2 32 44 / 0.25);
}

/* Examples */
.feature-examples-section {
  border-top: 1px solid #e2e8f0;
  background: #ffffff;
  padding: clamp(4.5rem, 8vw, 7rem) 0;
}

.feature-examples-grid {
  display: grid;
  gap: 1.1rem;
  margin-top: 3rem;
}

.feature-example {
  border: 1px solid #e2e8f0;
  border-radius: 0.9rem;
  background: #f8fafc;
  padding: 1.4rem 1.3rem 1.5rem;
}

.feature-example > header {
  display: flex;
  gap: 0.9rem;
  align-items: flex-start;
  justify-content: space-between;
}

.feature-example__meta {
  display: flex;
  flex: none;
  gap: 0.45rem;
  align-items: center;
}

.feature-example__tag {
  border-radius: 999px;
  background: #eef2f6;
  padding: 0.24rem 0.62rem;
  color: #475569;
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
}

.feature-example__status {
  display: inline-flex;
  width: fit-content;
  align-items: center;
  gap: 0.3rem;
  border-radius: 999px;
  background: #e6f7ed;
  padding: 0.22rem 0.6rem;
  color: #15803d;
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
}

.feature-example__status::before {
  width: 0.34rem;
  height: 0.34rem;
  border-radius: 999px;
  background: #16a34a;
  content: "";
}

.feature-example h3 {
  color: #0f172a;
  font-size: 1.05rem;
  font-weight: 900;
  line-height: 1.3;
}

.feature-example ol {
  display: grid;
  gap: 0.55rem;
  margin-top: 1.2rem;
}

.feature-example li {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 0.65rem;
  align-items: start;
}

.feature-example li i {
  display: grid;
  width: 1.45rem;
  height: 1.45rem;
  margin-top: 0.05rem;
  place-items: center;
  border-radius: 999px;
  background: #1c1917;
  color: #ffffff;
  font-size: 0.68rem;
  font-style: normal;
  font-weight: 900;
}

.feature-example li span {
  color: #334155;
  font-size: 0.85rem;
  font-weight: 700;
  line-height: 1.5;
}

.feature-xstep--wait i {
  background: #b45309;
}

.feature-xstep--sms i {
  background: #1d4ed8;
}

.feature-xstep--email i {
  background: #0b6e88;
}

.feature-xstep--condition i {
  background: #6d28d9;
}

.feature-xstep--task i {
  background: #15803d;
}

/* Related features */
.feature-related-section {
  border-top: 1px solid #e2e8f0;
  background: #f5f7fa;
  padding: clamp(4.5rem, 8vw, 7rem) 0;
}

.feature-related-section > .feature-shell {
  display: grid;
  gap: 2rem 4rem;
  align-items: start;
}

.feature-related-section .feature-section-heading--split {
  display: block;
  max-width: 28rem;
}

.feature-related-section .feature-section-heading--split > p {
  margin-top: 1rem;
}

/* One bordered list of links, not a grid of cards. */
.feature-related-grid {
  overflow: hidden;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #ffffff;
}

.feature-related-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.3rem 1.5rem;
  align-items: center;
  align-content: start;
  border-top: 1px solid #e2e8f0;
  padding: 1.15rem 1.4rem;
  text-decoration: none;
  transition: background-color 150ms ease;
}

.feature-related-card:first-child {
  border-top: 0;
}

.feature-related-card:hover,
.feature-related-card:focus-visible {
  background: #f8fafc;
}

.feature-related-card > span {
  grid-column: 1;
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 900;
  line-height: 1.3;
}

.feature-related-card > p {
  grid-column: 1;
  color: #475569;
  font-size: 0.85rem;
  line-height: 1.6;
}

.feature-related-card > em {
  display: inline-flex;
  grid-column: 2;
  grid-row: 1 / span 2;
  gap: 0.3rem;
  align-items: center;
  color: #475569;
  font-size: 0.8rem;
  font-style: normal;
  font-weight: 600;
  white-space: nowrap;
}

.feature-related-card > em i {
  display: inline-block;
  font-style: normal;
  transition: transform 150ms ease;
}

.feature-related-card:hover > em i {
  transform: translateX(2px);
}

@media (min-width: 980px) {
  .feature-related-section > .feature-shell {
    grid-template-columns: minmax(0, 0.8fr) minmax(0, 1.2fr);
  }
}

/* Story pages: the curated solution, guide and comparison links under the Related features intro. */
.feature-read-next {
  margin-top: 2rem;
  border-top: 1px solid #e2e8f0;
  padding-top: 1.25rem;
}

.feature-read-next h3 {
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
  line-height: 1.3;
}

.feature-read-next ul {
  display: grid;
  gap: 0.15rem;
  margin-top: 0.6rem;
}

.feature-read-next a {
  display: block;
  min-height: 2.25rem;
  padding-block: 0.45rem;
  color: #0e7490;
  font-size: 0.9rem;
  font-weight: 650;
  line-height: 1.35;
  text-decoration: none;
}

/* Inline, not inline-block: an atomic box would add a line-break chance before the arrow. */
.feature-read-next a span {
  margin-left: 0.35em;
}

.feature-read-next a:hover,
.feature-read-next a:focus-visible {
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}

.feature-read-next a:focus-visible {
  outline: 2px solid #0e7490;
  outline-offset: 2px;
  border-radius: 4px;
}

@media (max-width: 560px) {
  .feature-related-card {
    grid-template-columns: minmax(0, 1fr);
  }

  .feature-related-card > em {
    grid-column: 1;
    grid-row: auto;
    margin-top: 0.3rem;
  }
}

/* Final CTA (navy closer) */
.feature-final-cta {
  border-top: 1px solid #dbe4ee;
  background: #07101f;
  padding: clamp(3.5rem, 6vw, 5rem) 0;
  text-align: left;
}

/* Message on the left, actions and the trial line on the right (stacked below 980px). */
.feature-final-cta__inner {
  display: grid;
  gap: 1.15rem 4rem;
  align-items: start;
  justify-items: start;
}

@media (max-width: 979px) {
  .feature-final-cta__inner > .feature-hero-actions {
    order: 2;
  }

  .feature-final-cta__inner > .feature-final-cta__trial {
    order: 3;
  }
}

/* B1.1: one column here, so the 1.15rem row gap already spaces the terms from the buttons; their own top margin made it
   34px. Phones (<= 640px) have their own rule below. */
@media (min-width: 641px) and (max-width: 979px) {
  .feature-final-cta__trial :deep(.trial-disclosure) {
    margin-top: 0;
  }
}

@media (min-width: 980px) {
  .feature-final-cta__inner {
    grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr);
    row-gap: 0;
  }

  .feature-final-cta__inner > h2 {
    grid-column: 1;
    grid-row: 1;
    margin-top: 0;
  }

  .feature-final-cta__inner > .feature-final-cta__copy {
    grid-column: 1;
    grid-row: 2;
    margin-top: 1.1rem;
  }

  .feature-final-cta__inner > .feature-hero-actions {
    grid-column: 2;
    grid-row: 1;
    align-self: end;
  }

  .feature-final-cta__inner > .feature-final-cta__trial {
    grid-column: 2;
    grid-row: 2;
    margin-top: 0.5rem;
  }
}

.feature-final-cta h2 {
  max-width: 44rem;
  color: #ffffff;
}

.feature-final-cta__copy {
  max-width: 40rem;
  color: #c3cedd;
  font-size: 1.05rem;
  line-height: 1.7;
}

.feature-final-cta__trial .feature-textlink {
  margin-top: 0.85rem;
}

/* Phones stack the buttons, which put "Request a demo" between the trial button and its terms: the row and the
   terms block dissolve into the grid so the order reads trial button, terms, demo, pricing link (legal B1.1). */
@media (max-width: 640px) {
  .feature-final-cta__inner > .feature-hero-actions,
  .feature-final-cta__inner > .feature-final-cta__trial {
    display: contents;
  }

  .feature-final-cta__inner .feature-primary {
    order: 2;
  }

  .feature-final-cta__inner :deep(.trial-disclosure) {
    order: 3;
    margin-top: -0.4rem;
  }

  .feature-final-cta__inner .feature-final-cta__ghost {
    order: 4;
    justify-self: stretch;
  }

  .feature-final-cta__inner .feature-textlink {
    order: 5;
    margin-top: 0;
  }
}

.feature-final-cta .feature-hero-actions--center,
.is-story .feature-final-cta .feature-hero-actions--center {
  justify-content: flex-start;
  margin-top: 0;
}

.feature-final-cta__ghost {
  display: inline-flex;
  min-height: 3rem;
  align-items: center;
  justify-content: center;
  border: 1px solid #33506e;
  border-radius: 0.375rem;
  background: transparent;
  padding: 0.75rem 1.2rem;
  color: #e8eef8;
  font-size: 0.88rem;
  font-weight: 900;
  line-height: 1;
  text-decoration: none;
  transition: border-color 150ms ease, color 150ms ease, transform 150ms ease;
}

.feature-final-cta__ghost:hover,
.feature-final-cta__ghost:focus-visible {
  border-color: #67e8f9;
  color: #67e8f9;
  transform: translateY(-1px);
}

.feature-textlink {
  display: inline-flex;
  min-height: 44px;
  gap: 0.35rem;
  align-items: center;
  color: #67e8f9;
  font-size: 0.86rem;
  font-weight: 850;
  text-decoration: none;
}

.feature-textlink:hover,
.feature-textlink:focus-visible {
  text-decoration: underline;
  text-underline-offset: 0.25rem;
}

/* Sticky mobile CTA */
.feature-trial-note {
  flex: 1 1 100%;
  margin-top: 0;
}

/* B1.1: the row gap already spaces the terms from the trial button (18px on phones); the line's own top margin made it 29px. */
.feature-mid-cta__inner > .feature-trial-note :deep(.trial-disclosure) {
  margin-top: 0;
}

/* B1.1 at 900px+: paragraph left, trial button and its terms stacked in column 2 (same layout as the story band). */
@media (min-width: 900px) {
  .feature-mid-cta__inner {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.75rem 2.5rem;
  }

  .feature-mid-cta__inner > p {
    grid-column: 1;
    grid-row: 1 / span 2;
  }

  .feature-mid-cta__inner > a {
    grid-column: 2;
    grid-row: 1;
    justify-self: start;
    align-self: end;
  }

  .feature-mid-cta__inner > .feature-trial-note {
    grid-column: 2;
    grid-row: 2;
    max-width: 22rem;
    align-self: start;
    text-align: left;
  }
}

.feature-sticky-cta {
  flex-wrap: wrap;
  position: fixed;
  z-index: 40;
  right: 0;
  bottom: 0;
  left: 0;
  display: none;
  gap: 0.6rem;
  border-top: 1px solid #dbe4ee;
  background: rgb(255 255 255 / 0.96);
  padding: 0.7rem 0.9rem calc(0.7rem + env(safe-area-inset-bottom));
  backdrop-filter: blur(10px);
  transform: translateY(110%);
  visibility: hidden;
  transition: transform 0.25s ease, visibility 0s linear 0.25s;
}

.feature-sticky-cta.is-shown {
  transform: none;
  visibility: visible;
  transition: transform 0.25s ease;
}

@media (prefers-reduced-motion: reduce) {
  .feature-sticky-cta,
  .feature-sticky-cta.is-shown {
    transition: none;
  }
}

.feature-sticky-cta .feature-primary,
.feature-sticky-cta .feature-secondary {
  flex: 1;
  min-height: 2.75rem;
  padding: 0.65rem 0.8rem;
  font-size: 0.8rem;
}

/* Dark theme for v2 sections */
.dark .feature-capabilities-section,
.dark .feature-related-section {
  border-color: #243247;
  background: #0a1220;
}

.dark .feature-examples-section {
  border-color: #243247;
  background: #07101f;
}

.dark .feature-capabilities-grid article,
.dark .feature-related-grid {
  border-color: #243247;
  background: #07101f;
}

.dark .feature-related-card {
  border-top-color: #243247;
}

.dark .feature-read-next {
  border-top-color: #243247;
}

.dark .feature-read-next h3 {
  color: #ffffff;
}

.dark .feature-read-next a {
  color: #67e8f9;
}

.dark .feature-read-next a:focus-visible {
  outline-color: #67e8f9;
}

.dark .feature-related-card:hover,
.dark .feature-related-card:focus-visible {
  background: #0d1829;
}

.dark .feature-capabilities-grid article > span {
  background: #0c2a3a;
  color: #67e8f9;
}

.dark .feature-capabilities-grid h3,
.dark .feature-related-card > span,
.dark .feature-example h3 {
  color: #ffffff;
}

.dark .feature-capabilities-grid p,
.dark .feature-related-card > p,
.dark .feature-example li span {
  color: #cbd5e1;
}

.dark .feature-example {
  border-color: #243247;
  background: #0a1220;
}

.dark .feature-example__tag {
  background: #1b2942;
  color: #94a3b8;
}

.dark .feature-example__status {
  background: #052e16;
  color: #4ade80;
}

.dark .feature-example li i {
  background: #e2e8f0;
  color: #0f172a;
}

.dark .feature-xstep--wait i {
  background: #fbbf24;
}

.dark .feature-xstep--sms i {
  background: #93c5fd;
}

.dark .feature-xstep--email i {
  background: #67e8f9;
}

.dark .feature-xstep--condition i {
  background: #c4b5fd;
}

.dark .feature-xstep--task i {
  background: #4ade80;
}

.dark .feature-related-card > em {
  color: #a8b6cb;
}

.dark .feature-sticky-cta {
  border-color: #243247;
  background: rgb(7 16 31 / 0.96);
}

@media (max-width: 760px) {
  .feature-sticky-cta {
    display: flex;
  }

  .feature-final-cta {
    padding-bottom: 9rem;
  }
}

@media (min-width: 760px) {
  .feature-capabilities-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .feature-examples-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.dark .feature-reference > span { color: #67e8f9; }
</style>

<style scoped>
.intake-table { overflow-x: auto; margin: 1.5rem 0; }
.intake-table table { width: 100%; min-width: 640px; border-collapse: collapse; text-align: start; }
.intake-table th, .intake-table td { padding: 1rem; border-bottom: 1px solid #cbd5e1; vertical-align: top; }
.intake-table caption { text-align: start; padding: .5rem 0; font-weight: 600; }
.intake-table:focus-visible { outline: 2px solid #0d9488; outline-offset: 3px; }


.dark .feature-section-heading:not(.feature-section-heading--split) > p,
.dark .feature-flow-list li > span,
.dark .feature-use-cases-grid article > span { color: #67e8f9; }

/* Grids sized to their item count, so two or four cards never leave an empty slot or a lone card
   on the last row. */
@media (min-width: 760px) {
  .feature-examples-grid[data-count="1"] {
    grid-template-columns: minmax(0, 1fr);
  }

  .feature-examples-grid[data-count="2"],
  .feature-examples-grid[data-count="4"],
  .feature-capabilities-grid[data-count="4"],
  .feature-flow-list[data-count="4"],
  .feature-use-cases-grid[data-count="4"] {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* Five cards: three on the first row, two wider ones under them. */
  .feature-capabilities-grid[data-count="5"],
  .feature-examples-grid[data-count="5"] {
    grid-template-columns: repeat(6, minmax(0, 1fr));
  }

  .feature-capabilities-grid[data-count="5"] > *,
  .feature-examples-grid[data-count="5"] > * {
    grid-column: span 2;
  }

  .feature-capabilities-grid[data-count="5"] > :nth-child(n + 4),
  .feature-examples-grid[data-count="5"] > :nth-child(n + 4) {
    grid-column: span 3;
  }
}

@media (min-width: 1180px) {
  .feature-flow-list[data-count="4"] {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}
/* Story pages, template v3 (whiteboard, Oct 22): the product leads. The hero stacks: a short
   headline and the actions centred, the three points as a strip, then the app at full width.
   Section headings drop to one calmer scale, the definition reads as a note, and the mid-page
   call to action is a quiet row instead of a gradient band. */
.is-story .feature-detail-hero {
  padding: 8rem 0 0;
  background: linear-gradient(#ffffff 0%, #f6f9fc 100%);
}
.is-story .feature-breadcrumb { display: flex; justify-content: center; margin-bottom: 1.6rem; }
.is-story .feature-hero-grid { display: block; }
.is-story .feature-hero-copy { max-width: 52rem; margin: 0 auto; text-align: center; }
.is-story .feature-hero-copy h1 {
  max-width: none;
  font-size: clamp(2.4rem, 4.6vw, 3.9rem);
  font-weight: 760;
  letter-spacing: -0.032em;
  line-height: 1.04;
}
.is-story .feature-intro { margin-inline: auto; max-width: 40rem; }
.feature-hero-plan-note { max-width: 40rem; margin: 0.9rem 0 0; color: #475569; font-size: 0.875rem; line-height: 1.6; }
.is-story .feature-hero-plan-note { margin-inline: auto; }
.dark .feature-hero-plan-note { color: #94a3b8; }
.is-story .feature-hero-actions { justify-content: center; }
.is-story .feature-hero-copy :deep(.trial-disclosure) { margin-inline: auto; text-align: center; }
.is-story .feature-hero-facts {
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  max-width: 68rem;
  margin: 0 auto;
  border: 0;
  border-top: 1px solid #dbe4ee;
  padding: 1.6rem 0 2.4rem;
  text-align: left;
}
.is-story .feature-hero-facts > div {
  grid-template-columns: minmax(0, 1fr);
  gap: 0.3rem;
  border-top: 0;
  border-left: 1px solid #dbe4ee;
  padding: 0.2rem 1.4rem;
}
.is-story .feature-hero-facts > div:first-child { border-left: 0; }
.is-story .feature-hero-facts dd { font-size: 0.9rem; font-weight: 600; color: #334155; }
.is-story .feature-hero-story {
  position: relative;
  max-width: 50rem;
  margin: 3.4rem auto 2.6rem;
  transform: none;
}
/* The hero window floats on the page: no stage box behind it. */
.is-story .feature-hero-story.scs,
.is-story .feature-hero-story.sst { background: transparent; }
.is-story .feature-hero-story.scs::before,
.is-story .feature-hero-story.sst::before { display: none; }
@media (min-width: 900px) {
  .is-story .feature-hero-story { zoom: 1.16; }
}
@media (min-width: 1200px) {
  .is-story .feature-hero-story { zoom: 1.3; }
}
/* An app-screen hero (StoryReplicaStage `app`) is drawn at the app's own scale, so it gets the
   width instead of a zoom: the whole Invoices screen with its sidebar from 1080px, the collapsed
   sidebar on tablets, the phone layout under 600px. */
.is-story .feature-hero-story.feature-hero-story--app { zoom: 1; max-width: 78rem; margin-top: 1.25rem; margin-bottom: 3.6rem; }
/* Big screens: the app window outgrows the text column (up to 90rem), centred on the page. */
@media (min-width: 1600px) {
  .is-story .feature-hero-story.feature-hero-story--app { left: 50%; width: min(90rem, 100vw - 8rem); max-width: none; translate: -50% 0; }
}
/* With the app as the hero, the copy above it is kept short so the table starts in the first
   screen: no eyebrow (the breadcrumb already names the page), a wider intro, a slightly smaller H1.
   Desktop only: on phones the hero copy is unchanged (a different H1 size re-wraps when the web font swaps in). */
@media (min-width: 769px) {
  .is-story .feature-detail-hero:has(.feature-hero-story--app) { padding-top: 8.25rem; }
  .is-story .feature-detail-hero:has(.feature-hero-story--app) .feature-breadcrumb { margin-bottom: 1.1rem; }
  .is-story .feature-detail-hero:has(.feature-hero-story--app) .feature-hero-copy { max-width: 66rem; }
  .is-story .feature-detail-hero:has(.feature-hero-story--app) .feature-intro { max-width: 60rem; }
  .is-story .feature-detail-hero:has(.feature-hero-story--app) .feature-hero-copy h1 { font-size: clamp(2.3rem, 3.8vw, 3.25rem); }
}
.is-story .feature-definition-section { border-top: 1px solid #e2e8f0; background: #ffffff; padding: 4.5rem 0; }
.is-story .feature-definition-grid { grid-template-columns: minmax(0, 1fr); max-width: 52rem; gap: 1rem; }
.is-story .feature-definition-grid h2 { font-size: clamp(1.45rem, 2.2vw, 1.8rem); font-weight: 700; letter-spacing: -0.015em; }
.is-story .feature-definition-copy { font-size: 1.12rem; color: #1e293b; }
.is-story :deep(section h2) {
  font-size: clamp(1.9rem, 3.1vw, 2.65rem);
  font-weight: 740;
  letter-spacing: -0.026em;
  line-height: 1.08;
}
.is-story .feature-definition-grid h2 { font-size: clamp(1.45rem, 2.2vw, 1.8rem); }
.is-story .feature-mid-cta--quiet { border-block: 1px solid #e2e8f0; background: #f8fafc; }
.is-story .feature-mid-cta--quiet .feature-mid-cta__inner {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.4rem 2rem;
  align-items: center;
  padding: 1.7rem 0;
}
/* B1.1: the terms sit in column 2 directly under the trial button (left-aligned to it, capped at 22rem so they don't
   widen the column much); the paragraph spans both rows on the left. */
.is-story .feature-mid-cta--quiet .feature-mid-cta__inner > p { grid-column: 1; grid-row: 1 / span 2; max-width: 40rem; color: #334155; font-size: 1.02rem; font-weight: 500; }
.is-story .feature-mid-cta--quiet .feature-mid-cta__inner > a { grid-column: 2; grid-row: 1; justify-self: start; align-self: end; }
.is-story .feature-mid-cta--quiet .feature-trial-note { grid-column: 2; grid-row: 2; flex: none; max-width: 22rem; align-self: start; text-align: left; }
.is-story .feature-mid-cta--quiet .feature-mid-cta__inner > p strong { display: block; margin-bottom: 0.15rem; color: #020617; font-size: 1.15rem; font-weight: 700; }
/* One column below 900px: with the 22rem terms column beside it the paragraph would be squeezed. */
@media (max-width: 899px) {
  .is-story .feature-mid-cta--quiet .feature-mid-cta__inner { grid-template-columns: minmax(0, 1fr); gap: 0.9rem; }
  .is-story .feature-mid-cta--quiet .feature-mid-cta__inner > p { grid-row: 1; }
  .is-story .feature-mid-cta--quiet .feature-mid-cta__inner > a { grid-column: 1; grid-row: 2; justify-self: stretch; }
  .is-story .feature-mid-cta--quiet .feature-trial-note { grid-column: 1; grid-row: 3; }
}
@media (min-width: 640px) and (max-width: 899px) {
  .is-story .feature-mid-cta--quiet .feature-mid-cta__inner > a { justify-self: start; }
}
.is-story :deep(.fru > div) { max-width: 84rem; padding-inline: 0; width: min(100% - 3rem, 84rem); }
.dark .is-story .feature-detail-hero { background: linear-gradient(#020617 0%, #07101f 100%); }
.dark .is-story .feature-hero-facts,
.dark .is-story .feature-hero-facts > div { border-color: #1e293b; }
.dark .is-story .feature-hero-facts dd { color: #cbd5e1; }
.dark .is-story .feature-definition-section { border-color: #1e293b; background: #020617; }
.dark .is-story .feature-definition-copy { color: #e2e8f0; }
.dark .is-story .feature-mid-cta--quiet { border-color: #1e293b; background: #0b1220; }
.dark .is-story .feature-mid-cta--quiet .feature-mid-cta__inner > p { color: #cbd5e1; }
.dark .is-story .feature-mid-cta--quiet .feature-mid-cta__inner > p strong { color: #f8fafc; }
@media (max-width: 760px) {
  .is-story .feature-detail-hero { padding-top: 6.5rem; }
  .is-story .feature-hero-copy { text-align: left; }
  .is-story .feature-breadcrumb { justify-content: flex-start; }
  .is-story .feature-intro { margin-inline: 0; }
  .is-story .feature-hero-plan-note { margin-inline: 0; }
  .is-story .feature-hero-actions { justify-content: flex-start; }
  .is-story .feature-hero-copy :deep(.trial-disclosure) { margin-inline: 0; text-align: left; }
  .is-story .feature-hero-facts { grid-template-columns: minmax(0, 1fr); padding: 0.4rem 0 1.6rem; }
  .is-story .feature-hero-facts > div { border-left: 0; border-top: 1px solid #dbe4ee; padding: 0.7rem 0; }
  /* The list's own top border already draws the first line. */
  .is-story .feature-hero-facts > div:first-child { border-top: 0; }
  .is-story .feature-hero-story { margin-top: 2.2rem; }
  .is-story .feature-hero-story.scs { height: 580px; }
  /* Phones: the product comes right after the headline so it shows on the first screen; the
     intro and actions follow it (the sticky trial bar keeps a start button in reach). */
  .is-story .feature-hero-grid { display: flex; flex-direction: column; align-items: stretch; gap: 0; }
  .is-story .feature-hero-copy { display: contents; }
  .is-story .feature-hero-grid > * ,
  .is-story .feature-hero-copy > * { text-align: left; }
  .is-story .feature-hero-copy h1 { font-size: clamp(1.9rem, 8.6vw, 2.4rem); line-height: 1.06; }
  .is-story .feature-hero-story { order: 1; margin: 1rem 0 0.4rem; }
  /* The terms live inside .feature-hero-actions, so they move with it. */
  .is-story .feature-intro,
  .is-story .feature-hero-actions,
  .is-story .feature-hero-plan-note { order: 2; }
}
</style>
